import React, { useState, useEffect, useRef } from 'react';
import { Project, FileMetadata, ConversationEntry } from '../services/enhancedApi';
import enhancedAPI from '../services/enhancedApi';
import ChartVisualization from './ChartVisualization';
import PDFExportDialog from './PDFExportDialog';
import { FileUploadIcon, SendIcon, FileIcon, TrashIcon } from './Icons';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './ProjectChatView.css';

// Utility function to format recommendation text with proper lists and formatting
const formatRecommendationText = (text: string): React.ReactElement => {
  if (!text || typeof text !== 'string') {
    return <span>{text}</span>;
  }

  // Clean up redundant headers first
  let cleanedText = text
    .replace(/^Strategic Recommendation:\s*/i, '')
    .replace(/^Strategic Implication for FAB:\s*/i, '')
    .replace(/^Recommendation:\s*/i, '')
    .trim();

  // Split text into sections and handle bullet points
  const parts = cleanedText.split(/(?=•|\d+\.|Business Impact:|FAB-Specific Action Plan:|Executive Actions Required:)/);
  
  return (
    <div className="formatted-recommendation">
      {parts.map((part, index) => {
        const trimmedPart = part.trim();
        if (!trimmedPart) return null;

        // Handle section headers
        if (trimmedPart.startsWith('Business Impact:') || 
            trimmedPart.startsWith('FAB-Specific Action Plan:') || 
            trimmedPart.startsWith('Executive Actions Required:')) {
          const [header, ...content] = trimmedPart.split(':');
          return (
            <div key={index} className="recommendation-section">
              <h6 className="section-header">{header}:</h6>
              <div className="section-content">
                {formatRecommendationText(content.join(':'))}
              </div>
            </div>
          );
        }

        // Handle bullet points
        if (trimmedPart.includes('•')) {
          const bulletItems = trimmedPart.split('•').filter(item => item.trim());
          const [intro, ...items] = bulletItems;
          
          return (
            <div key={index} className="bullet-section">
              {intro && intro.trim() && <p className="intro-text">{intro.trim()}</p>}
              <ul className="bullet-list">
                {items.map((item, itemIdx) => (
                  <li key={itemIdx} className="bullet-item">{item.trim()}</li>
                ))}
              </ul>
            </div>
          );
        }

        // Handle numbered lists
        if (/^\d+\./.test(trimmedPart)) {
          const lines = trimmedPart.split('\n').filter(line => line.trim());
          const numberedItems = lines.filter(line => /^\d+\./.test(line.trim()));
          const otherText = lines.filter(line => !/^\d+\./.test(line.trim())).join(' ');
          
          return (
            <div key={index} className="numbered-section">
              {otherText && <p className="intro-text">{otherText}</p>}
              <ol className="numbered-list">
                {numberedItems.map((item, itemIdx) => (
                  <li key={itemIdx} className="numbered-item">
                    {item.replace(/^\d+\.\s*/, '')}
                  </li>
                ))}
              </ol>
            </div>
          );
        }

        // Regular paragraph text
        return <p key={index} className="recommendation-paragraph">{trimmedPart}</p>;
      }).filter(Boolean)}
    </div>
  );
};

interface Message {
  id: string;
  type: 'user' | 'system' | 'execution_plan' | 'assistant' | 'processing';
  content: string;
  timestamp: string;
  executionPlan?: any;
  queryId?: string;
  actionTaken?: boolean; // Track if Accept/Reject was clicked
}

interface PDFExportOptions {
  sections: string[];
  includeCharts: boolean;
  includeMetadata: boolean;
  includeRecommendations: boolean;
  includeNextSteps: boolean;
}

interface StructuredResultViewProps {
  result: any;
}

const StructuredResultView: React.FC<StructuredResultViewProps> = ({ result }) => {
  const [activeTab, setActiveTab] = useState('Overview');
  const areaNames = Object.keys(result.areas);
  // Ensure we have a valid tab selected
  React.useEffect(() => {
    if (areaNames.length > 0 && !result.areas[activeTab]) {
      setActiveTab(areaNames[0]);
    }
  }, [result, areaNames.join('|')]);
  console.log('[StructuredResultView] init. keys:', Object.keys(result), 'areaCount:', areaNames.length, 'areas:', areaNames);
  
  // Function to normalize area names for display
  const normalizeAreaName = (area: string): string => {
    const spaced = area.replace(/_/g, ' ');
    // Title Case each word
    const titleCased = spaced.replace(/\b\w/g, (l) => l.toUpperCase());
    // Special-case mappings
    return titleCased
      .replace(/cx insights/gi, 'Customer Insights');
  };
  
  const handleRefresh = () => {
    // This will trigger a refresh of the current query
    // The parent component should handle the actual refresh logic
    console.log('Refresh requested for current analysis');
    // You can implement the refresh logic here or pass it down from parent
  };
  
  return (
    <div className="structured-result">
      {/* AI Disclaimer */}
      <div className="ai-disclaimer">
        <strong>⚠️ Note:</strong> This feature is powered by AI. Outputs may contain inaccuracies. Please verify critical information before use.
      </div>
      
      {result.executive_summary && (
        <div className="result-section executive-summary">
          <h4>Executive Summary</h4>
          <p>{result.executive_summary}</p>
        </div>
      )}
      
      <div className="area-tabs">
        <div className="tab-header">
          {areaNames.map(area => (
            <button
              key={area}
              className={`tab-button ${activeTab === area ? 'active' : ''}`}
              onClick={() => { console.log('[StructuredResultView] tab->', area, 'area keys:', Object.keys(result.areas[area] || {})); setActiveTab(area); }}
            >
              {normalizeAreaName(area)}
            </button>
          ))}
        </div>
        
        <div className="tab-content">
          {result.areas[activeTab] && (
            <div className="area-content">
              <h5>{normalizeAreaName(activeTab)}</h5>
              
              {(result.areas[activeTab].summary || 
                result.areas[activeTab].content || 
                result.areas[activeTab].analysis ||
                result.areas[activeTab].text ||
                result.areas[activeTab].description ||
                result.areas[activeTab].insights) && (
                <div className="area-summary">
                  <p><strong>
                    {result.areas[activeTab].summary || 
                     result.areas[activeTab].content || 
                     result.areas[activeTab].analysis ||
                     result.areas[activeTab].text ||
                     result.areas[activeTab].description ||
                     result.areas[activeTab].insights}
                  </strong></p>
                </div>
              )}
              
              {result.areas[activeTab].details && (
                <div className="area-detail">
                  <p>{result.areas[activeTab].details}</p>
                </div>
              )}
              
              {(result.areas[activeTab].key_points || result.areas[activeTab].bullet_points) && 
               (result.areas[activeTab].key_points || result.areas[activeTab].bullet_points).length > 0 && (
                <div className="key-points">
                  <h6>Key Points:</h6>
                  <ul>
                    {(result.areas[activeTab].key_points || result.areas[activeTab].bullet_points).map((point: any, idx: number) => (
                      <li key={idx}>
                        {typeof point === 'string' ? (
                          point
                        ) : point && typeof point === 'object' && point.label ? (
                          <>
                            <strong>{point.label}:</strong> {point.value}
                            {point.subtext && <span className="text-muted" style={{marginLeft: '8px'}}>({point.subtext})</span>}
                          </>
                        ) : (
                          JSON.stringify(point)
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
                              )}
                
                {/* Display area-specific recommendations */}
                {((result.areas[activeTab]?.recommendations && result.areas[activeTab].recommendations.length > 0) ||
                  (result.sections?.areas?.[activeTab]?.recommendations && result.sections.areas[activeTab].recommendations.length > 0) ||
                  (result.areas[activeTab]?.area_recommendations && result.areas[activeTab].area_recommendations.length > 0) ||
                  (result.sections?.areas?.[activeTab]?.area_recommendations && result.sections.areas[activeTab].area_recommendations.length > 0) ||
                  (result.areas[activeTab]?.suggestions && result.areas[activeTab].suggestions.length > 0) ||
                  (result.sections?.areas?.[activeTab]?.suggestions && result.sections.areas[activeTab].suggestions.length > 0) ||
                  (result.areas[activeTab]?.actions && result.areas[activeTab].actions.length > 0) ||
                  (result.sections?.areas?.[activeTab]?.actions && result.sections.areas[activeTab].actions.length > 0) ||
                  (result.areas[activeTab]?.next_steps && result.areas[activeTab].next_steps.length > 0) ||
                  (result.sections?.areas?.[activeTab]?.next_steps && result.sections.areas[activeTab].next_steps.length > 0)) && (
                  <div className="area-recommendations">
                    <h6>Recommendations:</h6>
                    <ul>
                      {(result.areas[activeTab]?.recommendations || 
                        result.sections?.areas?.[activeTab]?.recommendations ||
                        result.areas[activeTab]?.area_recommendations ||
                        result.sections?.areas?.[activeTab]?.area_recommendations ||
                        result.areas[activeTab]?.suggestions ||
                        result.sections?.areas?.[activeTab]?.suggestions ||
                        result.areas[activeTab]?.actions ||
                        result.sections?.areas?.[activeTab]?.actions ||
                        result.areas[activeTab]?.next_steps ||
                        result.sections?.areas?.[activeTab]?.next_steps || [])
                        .sort((a: any, b: any) => {
                          const priorityOrder: { [key: string]: number } = { 'high': 3, 'medium': 2, 'low': 1 };
                          const priorityA = priorityOrder[a.priority?.toLowerCase() || 'medium'] || 2;
                          const priorityB = priorityOrder[b.priority?.toLowerCase() || 'medium'] || 2;
                          return priorityB - priorityA; // Sort by priority: highest to lowest
                        })
                        .map((rec: any, idx: number) => (
                        <li key={idx} className="area-recommendation-item">
                          {typeof rec === 'string' ? 
                            formatRecommendationText(rec) : 
                            formatRecommendationText(rec.description || rec.title || rec.action || rec.suggestion || JSON.stringify(rec))
                          }
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
                
                {/* Debug: Log area recommendations status */}
                {console.log(`[StructuredResultView] Area ${activeTab} Recommendations Debug:`, {
                  hasRecommendations: !!(result.areas[activeTab]?.recommendations || result.sections?.areas?.[activeTab]?.recommendations),
                  hasAreaRecommendations: !!(result.areas[activeTab]?.area_recommendations || result.sections?.areas?.[activeTab]?.area_recommendations),
                  hasSuggestions: !!(result.areas[activeTab]?.suggestions || result.sections?.areas?.[activeTab]?.suggestions),
                  hasActions: !!(result.areas[activeTab]?.actions || result.sections?.areas?.[activeTab]?.actions),
                  hasNextSteps: !!(result.areas[activeTab]?.next_steps || result.sections?.areas?.[activeTab]?.next_steps),
                  recommendationsCount: (result.areas[activeTab]?.recommendations || result.sections?.areas?.[activeTab]?.recommendations || []).length,
                  areaRecommendationsCount: (result.areas[activeTab]?.area_recommendations || result.sections?.areas?.[activeTab]?.area_recommendations || []).length,
                  suggestionsCount: (result.areas[activeTab]?.suggestions || result.sections?.areas?.[activeTab]?.suggestions || []).length,
                  actionsCount: (result.areas[activeTab]?.actions || result.sections?.areas?.[activeTab]?.actions || []).length,
                  nextStepsCount: (result.areas[activeTab]?.next_steps || result.sections?.areas?.[activeTab]?.next_steps || []).length,
                  recommendationsData: result.areas[activeTab]?.recommendations || result.sections?.areas?.[activeTab]?.recommendations,
                  areaRecommendationsData: result.areas[activeTab]?.area_recommendations || result.sections?.areas?.[activeTab]?.area_recommendations,
                  suggestionsData: result.areas[activeTab]?.suggestions || result.sections?.areas?.[activeTab]?.suggestions,
                  actionsData: result.areas[activeTab]?.actions || result.sections?.areas?.[activeTab]?.actions,
                  nextStepsData: result.areas[activeTab]?.next_steps || result.sections?.areas?.[activeTab]?.next_steps,
                  allAreaKeys: Object.keys(result.areas[activeTab] || {}),
                  sectionsAreaKeys: Object.keys(result.sections?.areas?.[activeTab] || {})
                })}
                
                {result.areas[activeTab].findings && (
                <div className="area-findings">
                  <h6>Findings:</h6>
                  <p>{result.areas[activeTab].findings}</p>
                </div>
              )}
              
              {/* Show charts with Key Metrics above them if available */}
              {(result.areas[activeTab].data_points || result.areas[activeTab].charts) && (
                <div className="data-visualization">
                  {/* Display Key Metrics above charts if available */}
                  {(result.areas[activeTab].key_metrics || result.areas[activeTab].kpis) && (result.areas[activeTab].key_metrics || result.areas[activeTab].kpis).length > 0 && (
                    <div className="chart-metrics-section">
                      <div className="chart-metrics-container">
                        {(result.areas[activeTab].key_metrics || result.areas[activeTab].kpis).map((kpi: any, idx: number) => {
                          const areaColorClass = activeTab.toLowerCase()
                            .replace(/[_\s]/g, '-')
                            .replace(/cx-insights/g, 'customer-insights');
                          return (
                            <div key={idx} className={`chart-metrics-card metrics-${areaColorClass}`}>
                              <div className="metrics-label">{kpi.label}</div>
                              <div className="metrics-value">{kpi.value}</div>
                              {kpi.change && (
                                <div className={`metrics-change ${kpi.trend || ''}`}>
                                  {kpi.change}
                                </div>
                              )}
                              {kpi.trend && (
                                <div className={`metrics-trend-indicator ${kpi.trend}`}>
                                  {kpi.trend === 'up' ? '↑' : kpi.trend === 'down' ? '↓' : '→'}
                                </div>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  )}
                  
                  <h6>Data Visualizations:</h6>
                  {/* Handle charts array format - check both result.areas and result.sections.areas */}
                  {((result?.sections?.areas?.[activeTab]?.charts && Array.isArray(result.sections.areas[activeTab].charts)) || 
                    (result?.areas?.[activeTab]?.charts && Array.isArray(result.areas[activeTab].charts))) && (
                    <ChartVisualization 
                      charts={result?.sections?.areas?.[activeTab]?.charts || result?.areas?.[activeTab]?.charts || []}
                      tables={result?.sections?.areas?.[activeTab]?.tables || result?.areas?.[activeTab]?.tables || []}
                      onRefresh={handleRefresh}
                      canExport={true}
                    />
                  )}
                  {/* Handle data_points format - check both locations */}
                  {(result?.sections?.areas?.[activeTab]?.data_points || result?.areas?.[activeTab]?.data_points) && (
                    <ChartVisualization 
                      charts={Object.entries(
                        result?.sections?.areas?.[activeTab]?.data_points || 
                        result?.areas?.[activeTab]?.data_points || {}
                      ).map(([key, chartData]: [string, any]) => ({
                        chart_type: chartData.chart_type || chartData.type || 'bar',
                        chart_title: chartData.chart_title || key.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase()),
                        data: chartData.data || chartData,
                        labels: chartData.labels,
                        datasets: chartData.datasets
                      }))}
                      tables={result?.sections?.areas?.[activeTab]?.tables || result?.areas?.[activeTab]?.tables || []}
                      canExport={true}
                    />
                  )}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Display SWOT Analysis if available - only on Overview tab */}
      {(activeTab === 'overview' || activeTab === 'Overview') && result.swot_analysis && (
        <div className="result-section swot-analysis">
          <h4>SWOT Analysis</h4>
          <div className="swot-grid">
            {result.swot_analysis.strengths && (
              <div className="swot-section strengths">
                <h5>Strengths</h5>
                <ul>
                  {result.swot_analysis.strengths.map((item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.swot_analysis.weaknesses && (
              <div className="swot-section weaknesses">
                <h5>Weaknesses</h5>
                <ul>
                  {result.swot_analysis.weaknesses.map((item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.swot_analysis.opportunities && (
              <div className="swot-section opportunities">
                <h5>Opportunities</h5>
                <ul>
                  {result.swot_analysis.opportunities.map((item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
            {result.swot_analysis.threats && (
              <div className="swot-section threats">
                <h5>Threats</h5>
                <ul>
                  {result.swot_analysis.threats.map((item: string, idx: number) => (
                    <li key={idx}>{item}</li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Display global recommendations only on Overview tab */}
      {(activeTab === 'overview' || activeTab === 'Overview') && result.recommendations && result.recommendations.length > 0 && (
        <div className="result-section recommendations">
          <h5>Global Recommendations</h5>
          {result.recommendations
            .sort((a: any, b: any) => {
              const priorityOrder: { [key: string]: number } = { 'high': 3, 'medium': 2, 'low': 1 };
              const priorityA = priorityOrder[a.priority?.toLowerCase() || 'medium'] || 2;
              const priorityB = priorityOrder[b.priority?.toLowerCase() || 'medium'] || 2;
              return priorityB - priorityA; // Sort by priority: highest to lowest
            })
            .map((rec: any, idx: number) => (
            <div key={idx} className={`recommendation priority-${rec.priority?.toLowerCase() || 'medium'}`}>
              {rec.title ? (
                <>
                  <h6>{rec.title}</h6>
                  {rec.description && formatRecommendationText(rec.description)}
                  <div className="rec-meta">
                    {rec.impact && <span className="impact-tag">{rec.impact.toUpperCase()}</span>}
                    {(rec.timeline || rec.timeframe) && <span className="timeframe-tag">{rec.timeline || rec.timeframe}</span>}
                  </div>
                </>
              ) : (
                <div>{typeof rec === 'string' ? formatRecommendationText(rec) : (rec.description ? formatRecommendationText(rec.description) : <p>{JSON.stringify(rec)}</p>)}</div>
              )}
            </div>
          ))}
        </div>
      )}
      
      {result.next_steps && result.next_steps.length > 0 && (
        <div className="result-section next-steps">
          <h5>Next Steps</h5>
          <ol>
            {result.next_steps.map((step: string, idx: number) => (
              <li key={idx}>{step}</li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
};

interface ProjectChatViewProps {
  project: Project;
  onBack: () => void;
}

const ProjectChatView: React.FC<ProjectChatViewProps> = ({ project, onBack }) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputMessage, setInputMessage] = useState('');
  const [uploadedFiles, setUploadedFiles] = useState<FileMetadata[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [waitingForApproval, setWaitingForApproval] = useState(false);
  const [currentQueryId, setCurrentQueryId] = useState<string | null>(null);
  const [currentEntryId, setCurrentEntryId] = useState<string | null>(null); // Track current conversation entry for chat persistence
  const [queryProgress, setQueryProgress] = useState<any>(null);
  const [queryResult, setQueryResult] = useState<any>(null);
  const [eventSource, setEventSource] = useState<EventSource | null>(null);
  const [hasCheckedQueries, setHasCheckedQueries] = useState(false);
  const [hasShownCompletion, setHasShownCompletion] = useState<{ [queryId: string]: boolean }>({});
  // Ref to prevent duplicate completion handling due to rapid SSE events
  const shownCompletionsRef = useRef<Set<string>>(new Set());
  const [hasShownExecutionPlan, setHasShownExecutionPlan] = useState<{ [key: string]: boolean }>({});
  // Use a ref as well to prevent race conditions with state updates
  const shownExecutionPlansRef = useRef<Set<string>>(new Set());
  // Ref to prevent race conditions in checkForActiveQueries
  const isCheckingQueriesRef = useRef<boolean>(false);
  // Ref to prevent double initial query submission
  const hasSubmittedInitialQueryRef = useRef<boolean>(false);
  const [debugMode, setDebugMode] = useState(false);
  const [showPDFDialog, setShowPDFDialog] = useState(false);
  const [savedResults, setSavedResults] = useState<{ [key: string]: any }>({});
  const [isSyncMode, setIsSyncMode] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Reset the check flag when project changes
    setHasCheckedQueries(false);
    isCheckingQueriesRef.current = false;
    hasSubmittedInitialQueryRef.current = false; // Reset initial query submission flag
    
    console.log('ProjectChatView: Loading project:', project.project_id);
    console.log('Project has conversation history:', project.conversation_history?.length || 0);
    
    // Debug: Check what we actually received
    if (project.conversation_history && project.conversation_history.length > 0) {
      console.log('DEBUG: First conversation entry:', project.conversation_history[0]);
      const entriesWithResults = project.conversation_history.filter((e: any) => e.result);
      console.log('DEBUG: Entries with results:', entriesWithResults.length);
      if (entriesWithResults.length > 0) {
        console.log('DEBUG: First result structure:', entriesWithResults[0].result);
      }
    }
    
    // Load project files
    loadProjectFiles();
    
    // Load conversation history
    loadConversationHistory();
    
    // Check for active queries
    checkForActiveQueries();
  }, [project.project_id]); // Only depend on project ID, not the entire project object

  useEffect(() => {
    scrollToBottom();
  }, [messages]);
  
  useEffect(() => {
    // Cleanup SSE on unmount or project change
    return () => {
      if (eventSource) {
        console.log('Closing SSE stream for project:', project.project_id);
        eventSource.close();
        setEventSource(null);
      }
    };
  }, [project.project_id]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const loadProjectFiles = async () => {
    try {
      const files = await enhancedAPI.listProjectFiles(project.project_id);
      setUploadedFiles(files);
    } catch (error) {
      console.error('Failed to load project files:', error);
    }
  };

  const loadConversationHistory = (projectData?: Project) => {
    const proj = projectData || project;
    console.log('Loading conversation history for project:', proj.project_id);
    console.log('Conversation history entries:', proj.conversation_history?.length || 0);
    if (proj.conversation_history?.length > 0) {
      console.log('First entry has result:', !!proj.conversation_history[0].result);
      console.log('Last entry has result:', !!proj.conversation_history[proj.conversation_history.length - 1].result);
      
      // Debug: Log all entries
      proj.conversation_history.forEach((entry, idx) => {
        console.log(`🔍 Entry ${idx}:`, {
          entryId: entry.entry_id,
          hasResult: !!entry.result,
          resultStatus: entry.result?.status,
          hasSections: !!entry.result?.sections,
          sectionsStatus: entry.result?.sections?.status,
          sectionsKeys: entry.result?.sections ? Object.keys(entry.result.sections) : [],
          hasAreas: entry.result?.sections?.areas ? Object.keys(entry.result?.sections?.areas).length : 0
        });
      });
    }
    
    const conversationMessages: Message[] = [];
    let lastResult: any = null;
    
    // Keep track of current processing messages to preserve them
    const currentProcessingMessages = messages.filter(msg => 
      msg.content.includes('Processing') || 
      msg.content.includes('🔍') ||
      msg.type === 'execution_plan'
    );
    
    // Add initial query only if not already in conversation history
    // Check both the query text and ensure it's not a duplicate of the first entry
    const hasInitialQuery = proj.conversation_history?.length > 0 && 
      proj.conversation_history.some((entry: ConversationEntry, index: number) => {
        const queryText = typeof entry.query === 'string' 
          ? entry.query 
          : entry.query?.query || entry.query?.initial_query;
        // Only check if it matches initial query AND it's the first entry (avoid duplicates)
        return queryText === proj.initial_query && index === 0;
      });
    
    // Only add initial query if conversation history is empty or doesn't start with it
    if (proj.initial_query && !hasInitialQuery && proj.conversation_history?.length === 0) {
      conversationMessages.push({
        id: `initial-${proj.project_id}`,
        type: 'user',
        content: proj.initial_query,
        timestamp: proj.created_at
      });
    }
    
    // Add conversation history
    if (proj.conversation_history && proj.conversation_history.length > 0) {
      for (const entry of proj.conversation_history) {
        // Add user query
        const queryText = typeof entry.query === 'string' 
          ? entry.query 
          : entry.query?.query || entry.query?.initial_query || 'Unknown query';
          
        conversationMessages.push({
          id: `query-${entry.entry_id}`,
          type: 'user',
          content: queryText,
          timestamp: entry.timestamp
        });
        
        // Add saved chat messages from this conversation entry (User Story 11350)
        if (entry.chat_messages && entry.chat_messages.length > 0) {
          console.log(`📱 Loading ${entry.chat_messages.length} saved chat messages for entry ${entry.entry_id}`);
          // Sort chat messages by timestamp to maintain chronological order
          const sortedChatMessages = [...entry.chat_messages].sort((a, b) => 
            new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
          );
          
          for (const chatMsg of sortedChatMessages) {
            conversationMessages.push({
              id: `chat-${chatMsg.message_id}`,
              type: chatMsg.message_type as Message['type'],
              content: chatMsg.content,
              timestamp: chatMsg.timestamp,
              queryId: chatMsg.query_id,
              executionPlan: chatMsg.metadata?.executionPlan
            });
          }
        }
        
        // Add result confirmation if exists (don't include the actual result in chat)
        if (entry.result && entry.query_id) {
          // Skip failed results or results with empty/failed sections
          const isFailed = entry.result.status === 'failed' || 
                          (entry.result.sections && entry.result.sections.status === 'failed');
          if (isFailed) {
            console.log('⚠️ Skipping failed result for entry:', entry.entry_id);
            conversationMessages.push({
              id: `result-${entry.entry_id}`,
              type: 'system',
              content: '❌ Query failed. Please try again.',
              timestamp: entry.timestamp
            });
            continue; // Skip processing this failed result
          }
          
          // Track this query as having shown completion
          setHasShownCompletion(prev => ({ ...prev, [String(entry.query_id)]: true }));
          
          // Note: This message should already be saved as a chat message
          // If not found in chat_messages, it means it's from an old conversation
          const hasCompletionMessage = entry.chat_messages?.some(msg => 
            msg.content.includes('Query completed') || msg.content.includes('Results are displayed')
          );
          
          if (!hasCompletionMessage) {
            console.log('📝 Adding completion message from history for query:', entry.query_id);
            conversationMessages.push({
              id: `result-${entry.entry_id}`,
              type: 'system',
              content: '✅ Query completed. Results are displayed on the right.',
              timestamp: entry.timestamp
            });
          } else {
            console.log('📝 Skipping completion message from history - already exists for query:', entry.query_id);
          }
          // Keep track of the last result to display in the sidebar
          // Check for structured_response first, then result
          if (entry.result.structured_response) {
            lastResult = entry.result.structured_response;
            console.log('[History] Using structured_response. keys:', Object.keys(lastResult));
          } else if (entry.result.areas || entry.result.executive_summary) {
            lastResult = entry.result;
            console.log('[History] Using direct result. areaCount:', Object.keys(lastResult.areas || {}).length, 'exec len:', (lastResult.executive_summary || '').length);
          } else if (entry.result.sections || entry.result.visualizations) {
            console.log('=== TRANSFORMING OLD FORMAT ===');
            console.log('Has sections:', !!entry.result.sections);
            console.log('Has visualizations:', !!entry.result.visualizations);
            console.log('entry.result:', entry.result);
            
            // Transform old sections/visualizations format to areas/charts format
            const transformed: any = {
              executive_summary: '', // Will be filled below
              areas: {},
              charts: entry.result.visualizations || [],
              recommendations: [] // Will be filled below
            };
            
            // Convert sections to areas format
            if (entry.result.sections) {
              let sections: any = entry.result.sections;
              // Handle sections being a serialized JSON string
              if (typeof sections === 'string') {
                try {
                  sections = JSON.parse(sections);
                } catch (e) {
                  console.warn('Failed to parse sections JSON string, skipping transform');
                  sections = null;
                }
              }
              console.log('🔍 ProjectChatView: Processing sections structure:', {
                hasSections: !!sections,
                sectionsType: typeof sections,
                sectionsKeys: sections ? Object.keys(sections) : [],
                rawSections: sections
              });
              
              // The data shows sections is an object with the data at the top level
              if (sections && typeof sections === 'object' && !Array.isArray(sections)) {
                // Extract executive summary
                if (sections.executive_summary) {
                  transformed.executive_summary = sections.executive_summary;
                  console.log('✅ Found executive_summary in sections:', sections.executive_summary.substring(0, 100) + '...');
                }
                
                // Extract areas - they're directly in sections.areas
                if (sections.areas && typeof sections.areas === 'object') {
                  console.log('📊 Processing areas:', Object.keys(sections.areas));
                  // Transform each area
                  Object.entries(sections.areas).forEach(([areaName, areaData]: [string, any]) => {
                    const normalizedName = areaName.toLowerCase().replace(/[_\s]+/g, '_');
                    
                    // The area data has: content, summary, highlights, key_metrics, charts, data_points, recommendations
                    transformed.areas[normalizedName] = {
                      analysis: areaData.content || areaData.summary || '',
                      key_findings: areaData.highlights || areaData.key_points || areaData.key_findings || [],
                      data_points: areaData.data_points || {},
                      key_metrics: areaData.kpis || [],
                      charts: areaData.charts || [],
                      recommendations: areaData.recommendations || []
                    };
                    console.log(`  ✓ Transformed area ${areaName} -> ${normalizedName}`, {
                      hasAnalysis: !!transformed.areas[normalizedName].analysis,
                      keyFindingsCount: transformed.areas[normalizedName].key_findings.length,
                      metricsCount: transformed.areas[normalizedName].key_metrics.length,
                      chartsCount: transformed.areas[normalizedName].charts.length,
                      metricsData: transformed.areas[normalizedName].key_metrics
                    });
                  });
                  console.log('📈 Total areas transformed:', Object.keys(transformed.areas).length);
                  // Debug Key Metrics in detail
                  Object.entries(transformed.areas).forEach(([areaName, areaData]: [string, any]) => {
                    if (areaData.key_metrics && areaData.key_metrics.length > 0) {
                      console.log(`📊 Key Metrics for ${areaName}:`, areaData.key_metrics);
                    } else {
                      console.log(`⚠️ No Key Metrics for ${areaName}`);
                    }
                  });
                } else if (sections && Object.keys(sections).length > 0 && !sections.executive_summary) {
                  // Fallback: if sections itself appears to be an areas map
                  console.log('⚠️ sections.areas missing, attempting to treat sections as areas map');
                  Object.entries(sections).forEach(([areaName, areaData]: [string, any]) => {
                    if (typeof areaData === 'object') {
                      const normalizedName = areaName.toLowerCase().replace(/[_\s]+/g, '_');
                      transformed.areas[normalizedName] = {
                        analysis: areaData.content || areaData.summary || '',
                        key_findings: areaData.highlights || areaData.key_points || areaData.key_findings || [],
                        data_points: areaData.data_points || {},
                        key_metrics: areaData.key_metrics || areaData.kpis || [],
                        charts: areaData.charts || [],
                        recommendations: areaData.recommendations || []
                      };
                    }
                  });
                }
                
                // Extract recommendations
                if (sections.recommendations) {
                  transformed.recommendations = sections.recommendations;
                  console.log('💡 Found recommendations:', sections.recommendations.length, 'items');
                }
                
                // Extract SWOT analysis
                if (sections.swot_analysis) {
                  transformed.swot_analysis = sections.swot_analysis;
                  console.log('💡 Found SWOT analysis:', Object.keys(sections.swot_analysis));
                }
                
                // Extract executive insights
                if (sections.executive_insights) {
                  transformed.executive_insights = sections.executive_insights;
                  console.log('💡 Found executive insights');
                }
                
                // Extract agent responses
                if (sections.agent_responses) {
                  transformed.agent_responses = sections.agent_responses;
                  console.log('💡 Found agent responses');
                }
                
                // Extract risks and next_steps if present
                if (sections.risks) {
                  transformed.risks = sections.risks;
                }
                if (sections.next_steps) {
                  transformed.next_steps = sections.next_steps;
                }
              } 
              // Handle sections as array (older format)
              else if (Array.isArray(entry.result.sections)) {
                entry.result.sections.forEach((section: any) => {
                  const areaName = section.title?.toLowerCase().replace(/\s+/g, '_') || 'overview';
                  transformed.areas[areaName] = {
                    analysis: section.content || section.analysis || '',
                    key_findings: section.key_findings || [],
                    data_points: section.data_points || {}
                  };
                });
              }
            }
            
            // Only use this transformed result if it has actual content
            const hasValidContent = Object.keys(transformed.areas).length > 0 || 
                                   (transformed.executive_summary && transformed.executive_summary.length > 0);
            
            if (hasValidContent) {
              console.log('✅ Transformed result has valid content:', {
                oldKeys: Object.keys(entry.result),
                newKeys: Object.keys(transformed),
                areaNames: Object.keys(transformed.areas),
                executiveSummaryLength: transformed.executive_summary?.length || 0,
                recommendationsCount: transformed.recommendations?.length || 0
              });
              console.log('SETTING lastResult to transformed:', transformed);
              lastResult = transformed;
            } else {
              console.log('⚠️ Transformed result is empty, not using it')
            }
          } else if (entry.result.result) {
            lastResult = entry.result.result;
            console.log('[History] Using nested result field');
          } else {
            lastResult = entry.result;
            console.log('[History] Fallback to raw entry.result');
          }
        }
      }
    }
    
    // If we're currently processing, preserve those messages
    if (isProcessing && currentProcessingMessages.length > 0) {
      conversationMessages.push(...currentProcessingMessages);
    }
    
    // IMPORTANT: Preserve any execution plan messages that might be showing
    setMessages(prev => {
      // Find any existing execution plan messages and processing messages to preserve
      const executionPlanMessages = prev.filter(msg => msg.type === 'execution_plan');
      const currentProcessingMessages = prev.filter(msg => 
        msg.type === 'processing' && 
        !conversationMessages.some(histMsg => histMsg.content === msg.content)
      );
      
      console.log('🔄 Loading conversation history, preserving', executionPlanMessages.length, 'execution plan messages and', currentProcessingMessages.length, 'processing messages');
      
      // Create final message list with deduplication
      const messageMap = new Map();
      
      // Add conversation history messages
      conversationMessages.forEach(msg => {
        const key = `${msg.type}-${msg.content}-${msg.queryId || 'no-query'}`;
        if (!messageMap.has(key)) {
          messageMap.set(key, msg);
        }
      });
      
      // Add execution plan messages that don't exist (only one per project)
      executionPlanMessages.forEach(msg => {
        const key = `${msg.type}-project-${project.project_id}`; // Use project ID instead of query ID
        if (!messageMap.has(key)) {
          messageMap.set(key, msg);
        }
      });
      
      // Add current processing messages
      currentProcessingMessages.forEach(msg => {
        const key = `${msg.type}-${msg.content}-${msg.queryId || 'no-query'}`;
        if (!messageMap.has(key)) {
          messageMap.set(key, msg);
        }
      });
      
      // Convert back to array and sort by timestamp
      const finalMessages = Array.from(messageMap.values()).sort((a, b) => 
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime()
      );
      
      console.log(`📝 Final message count: ${finalMessages.length} (deduplicated from ${conversationMessages.length + executionPlanMessages.length + currentProcessingMessages.length})`);
      
      return finalMessages;
    });
    
    // Display the last result in the sidebar if available
    // Always set the result from saved conversation history
    if (lastResult) {
      console.log('📌 FINAL lastResult being set to queryResult:', JSON.stringify(lastResult, null, 2));
      console.log('📊 Result structure analysis:', {
        hasExecutiveSummary: !!lastResult.executive_summary,
        executiveSummaryPreview: lastResult.executive_summary ? lastResult.executive_summary.substring(0, 50) + '...' : 'NONE',
        hasAreas: !!lastResult.areas,
        areaCount: lastResult.areas ? Object.keys(lastResult.areas).length : 0,
        areaNames: lastResult.areas ? Object.keys(lastResult.areas) : [],
        hasCharts: !!lastResult.charts,
        chartCount: lastResult.charts ? lastResult.charts.length : 0,
        hasRecommendations: !!lastResult.recommendations,
        recommendationCount: lastResult.recommendations ? lastResult.recommendations.length : 0
      });
      
      // Directly set the result - don't check prevResult as we're loading from saved history
      console.log('🚀 CALLING setQueryResult with lastResult');
      setQueryResult(lastResult);
      // Also seed currentQueryId and savedResults so follow-ups have context even after reload
      const lastEntryWithResult = project.conversation_history
        .slice()
        .reverse()
        .find((e: any) => e.result);
      const derivedQueryId = lastEntryWithResult?.query_id || lastEntryWithResult?.result?.query_id;
      if (derivedQueryId) {
        setCurrentQueryId(String(derivedQueryId));
        setSavedResults(prev => ({ ...prev, [String(derivedQueryId)]: lastResult }));
        
        // Set current entry ID for new chat messages (User Story 11350)
        if (lastEntryWithResult) {
          setCurrentEntryId(lastEntryWithResult.entry_id);
          console.log('🔗 Set current entry ID from conversation history:', lastEntryWithResult.entry_id);
        }
      }
      
      // Also mark that we have a result so we don't show "Processing" messages
      setIsProcessing(false);
      
      // If the result has structured_response with areas, log it for debugging
      if (lastResult.areas) {
        console.log('Result has areas:', Object.keys(lastResult.areas));
      }
      if (lastResult.charts) {
        console.log('Result has charts:', lastResult.charts.length);
      }
    } else {
      console.log('🚫 No lastResult derived from history');
    }
  };

  const checkForActiveQueries = async () => {
    console.log('🔍 checkForActiveQueries called for project:', project.project_id, 'hasCheckedQueries:', hasCheckedQueries, 'isChecking:', isCheckingQueriesRef.current, 'isProcessing:', isProcessing, 'currentQueryId:', currentQueryId);
    
    // Don't check if we've already checked for this project, are currently checking, or already processing a query
    if (hasCheckedQueries || isCheckingQueriesRef.current || isProcessing || currentQueryId) {
      console.log('🚫 Already checked queries for this project, currently checking, processing, or have active query');
      return;
    }
    
    // Set the checking flag immediately to prevent race conditions
    isCheckingQueriesRef.current = true;
    setHasCheckedQueries(true);
    console.log('✅ Starting query check for project:', project.project_id);
    
    // Check if we need to check for active queries
    let shouldCheckQueries = false;
    let queryToCheck: string | null = null;
    
    console.log('Project conversation history length:', project.conversation_history.length);
    console.log('Has initial query:', !!project.initial_query);
    console.log('Has checked queries:', hasCheckedQueries);
    
    // Case 1: Initial query with no conversation history (or corrupted history)
    if (project.initial_query && !hasCheckedQueries) {
      // Check if we have valid conversation history with results
      const hasValidHistory = project.conversation_history.length > 0 && 
        project.conversation_history.some(entry => {
          // Check for result in multiple possible formats
          if (!entry.result) return false;
          
          // Check if result has actual content (not just status)
          const hasContent = (
            entry.result.structured_response ||
            entry.result.areas ||
            entry.result.executive_summary ||
            entry.result.sections ||
            entry.result.final_response ||
            entry.result.summary ||
            // Check for charts which indicate completed analysis
            (entry.result.charts && entry.result.charts.length > 0)
          );
          
          // Also check if status indicates completion
          const isCompleted = entry.result.status === 'completed' || entry.result.status === 'success';
          
          return hasContent || isCompleted;
        });
      
      if (!hasValidHistory) {
        shouldCheckQueries = true;
        console.log('Case 1: No valid history found, will re-run initial query for project:', project.project_id);
        
        // If no conversation history at all, we'll submit the query fresh
        if (project.conversation_history.length === 0) {
          console.log('No conversation history found - will submit initial query as new');
        }
      } else {
        console.log('Project has valid history with results, skipping re-run');
      }
    }
    
    // Case 2: Conversation history exists but last entry has query_id without result
    if (project.conversation_history.length > 0 && !hasCheckedQueries) {
      const lastEntry = project.conversation_history[project.conversation_history.length - 1];
      
      // Check if result exists - it might be in different formats
      const hasResult = lastEntry.result && (
        lastEntry.result.structured_response ||
        lastEntry.result.areas ||
        lastEntry.result.executive_summary ||
        lastEntry.result.sections ||
        lastEntry.result.status === 'completed'
      );
      
      if (lastEntry.query_id && !hasResult) {
        shouldCheckQueries = true;
        queryToCheck = lastEntry.query_id;
        console.log('Found incomplete query in conversation history:', queryToCheck);
      } else if (hasResult) {
        console.log('Project has completed results in conversation history, no need to check queries');
      }
    }
    
    if (shouldCheckQueries) {
      console.log('Checking for active queries for project:', project.project_id);
      
      // Mark that we've started checking to prevent duplicate messages
      setHasCheckedQueries(true);
      
      // Add processing message only once
      addMessage('system', '🔍 Processing your initial query...');
      setIsProcessing(true);
      
      // Keep track of retry count
      let retryCount = 0;
      const maxRetries = 10; // Increased from 5 to 10
      
      const checkQueries = async () => {
        try {
          // Special case: Only auto-submit when absolutely no history exists.
          // If backend later streams a completed result, we will render it without re-running.
          if (!queryToCheck && project.initial_query && project.conversation_history.length === 0 && !currentQueryId) {
            console.log('Submitting initial query as new for project:', project.project_id);
            
            // Additional protection: check if we've already submitted the initial query
            if (hasSubmittedInitialQueryRef.current) {
              console.log('🚫 Already submitted initial query, skipping to prevent double submission');
              return;
            }
            
            // Additional protection: check if we're already processing to prevent double submission
            if (isProcessing) {
              console.log('🚫 Already processing, skipping initial query submission');
              return;
            }
            
            // Mark that we're submitting the initial query
            hasSubmittedInitialQueryRef.current = true;
            
            try {
              // Submit the query using the correct API signature
              const result = await enhancedAPI.submitProjectQuery(
                project.project_id, 
                project.initial_query,
                false, // isFollowup
                undefined // parentEntryId
              );
              
              // Track the conversation entry ID for chat message persistence (User Story 11350)
              if (result.entry_id) {
                setCurrentEntryId(result.entry_id);
                console.log('🔗 Set current entry ID:', result.entry_id);
              }
              
              if (result && result.queryId) {
                console.log('Query submitted successfully:', result.queryId);
                setCurrentQueryId(result.queryId);
                
                // Start SSE streaming for the new query
                // The enhancedAPI will handle closing existing connections
                const sse = enhancedAPI.streamQueryStatus(result.queryId, (update) => {
                  console.log('SSE update for re-submitted query:', update);
                  handleQueryUpdate(update, result.queryId);
                });
                
                setEventSource(sse);
                return;
              }
            } catch (error) {
              console.error('Failed to submit initial query:', error);
              addMessage('system', '❌ Failed to re-run initial query. Please try refreshing the page.');
              setIsProcessing(false);
              return;
            }
          }
          
          // If we have a specific query ID to check, try to get its status directly
          if (queryToCheck) {
            console.log('Checking status for specific query:', queryToCheck);
            try {
              const queryStatus = await enhancedAPI.getQueryStatus(queryToCheck);
              if (queryStatus) {
                console.log('Found query status:', queryStatus);
                setCurrentQueryId(queryToCheck);
                
                if (queryStatus.status === 'processing') {
                  // Start SSE streaming
                  // The enhancedAPI will handle closing existing connections
                  const sse = enhancedAPI.streamQueryStatus(queryToCheck, (update) => {
                    console.log('SSE update for incomplete query:', update);
                    handleQueryUpdate(update, queryToCheck!);
                  });
                  
                  setEventSource(sse);
                  return;
                } else if (queryStatus.status === 'completed') {
                  // Query is complete, load the result
                  let finalResult = queryStatus.structured_response || 
                                    queryStatus.result || 
                                    queryStatus;
                  
                  if (typeof finalResult === 'string') {
                    try {
                      finalResult = JSON.parse(finalResult);
                    } catch (e) {
                      console.log('Result is not JSON, using as-is');
                    }
                  }
                  
                  setQueryResult(finalResult);
                  setIsProcessing(false);
                  // Don't add message here - it will be added by SSE update handler
                  
                  // Reload project to get updated conversation history
                  setTimeout(async () => {
                    const updatedProject = await enhancedAPI.getProject(project.project_id);
                    if (updatedProject.conversation_history.length > 0) {
                      loadConversationHistory(updatedProject);
                    }
                  }, 1000);
                  return;
                }
              }
            } catch (error) {
              console.log('Could not get status for specific query, falling back to general check');
            }
          }
          
          // First check for active queries
          const activeQueries = await enhancedAPI.getActiveQueries();
          console.log('Active queries:', activeQueries);
          
          let projectQuery = activeQueries.find(q => 
            q.context?.project_id === project.project_id
          );
          
          if (projectQuery) {
            console.log('Found active project query:', projectQuery);
            setCurrentQueryId(projectQuery.queryId);
            
            // Start SSE streaming for the active query
            // The enhancedAPI will handle closing existing connections
            console.log('Starting SSE stream for query:', projectQuery.queryId);
            const queryId = projectQuery.queryId;
            const sse = enhancedAPI.streamQueryStatus(queryId, (update) => {
              console.log('SSE update from checkForActiveQueries:', update);
              handleQueryUpdate(update, queryId);
            });
            
            setEventSource(sse);
          } else {
            // No active query, check recent query history
            console.log('No active query found, checking query history');
            const queryHistory = await enhancedAPI.getQueryHistory(50); // Increased from 10 to 50
            console.log('Query history length:', queryHistory.length);
            console.log('Looking for project_id:', project.project_id);
            
            // Find a recently completed query for this project
            const recentProjectQuery = queryHistory.find(q => {
              const matches = q.context?.project_id === project.project_id;
              if (matches) {
                console.log('Found matching query:', q.queryId, 'status:', q.status);
              }
              return matches && (q.status === 'completed' || q.status === 'failed' || q.status === 'processing');
            });
            
            if (recentProjectQuery) {
              console.log('Found recent project query:', recentProjectQuery);
              setCurrentQueryId(recentProjectQuery.queryId);
              
              // Handle different statuses
              if (recentProjectQuery.status === 'processing') {
                // Query is still processing, start SSE streaming
                console.log('Query still processing, starting SSE stream');
                // The enhancedAPI will handle closing existing connections
                const queryId = recentProjectQuery.queryId;
                const sse = enhancedAPI.streamQueryStatus(queryId, (update) => {
                  console.log('SSE update from query history check:', update);
                  handleQueryUpdate(update, queryId);
                });
                
                setEventSource(sse);
                return; // Don't retry if we found a processing query
              } else if (recentProjectQuery.status === 'completed') {
                let finalResult = recentProjectQuery.structured_response || 
                                  recentProjectQuery.result || 
                                  recentProjectQuery;
                
                // Parse if it's a JSON string
                if (typeof finalResult === 'string') {
                  try {
                    finalResult = JSON.parse(finalResult);
                  } catch (e) {
                    console.log('Result is not JSON, using as-is');
                  }
                }
                
                setQueryResult(finalResult);
                setIsProcessing(false);
                // Don't add duplicate message - SSE handler will add it
              } else if (recentProjectQuery.status === 'failed') {
                setIsProcessing(false);
                addMessage('system', `❌ Query failed: ${recentProjectQuery.error || 'Unknown error'}`);
              }
              
              // Reload project to get updated conversation history
              setTimeout(async () => {
                const updatedProject = await enhancedAPI.getProject(project.project_id);
                if (updatedProject.conversation_history.length > 0) {
                  loadConversationHistory(updatedProject);
                }
              }, 1000);
              return; // Don't retry if we found a completed/failed query
            } else if (retryCount < maxRetries) {
              // Query might still be initializing, wait a bit and check again
              retryCount++;
              console.log(`No query found yet, retry ${retryCount}/${maxRetries}...`);
              setTimeout(() => {
                checkQueries();
              }, 3000); // Increased from 2000 to 3000ms
            } else {
              // Max retries reached, stop checking
              console.log('Max retries reached, stopping query checks');
              setIsProcessing(false);
              addMessage('system', '⚠️ Unable to find query status. Please refresh the page.');
            }
          }
        } catch (error) {
          console.error('Failed to check for queries:', error);
          setIsProcessing(false);
          addMessage('system', '❌ Error checking query status. Please refresh the page.');
        }
      };
      
      // Start checking
      checkQueries();
    }
    
    // Reset the checking flag when done
    isCheckingQueriesRef.current = false;
  };

  const handleQueryUpdate = async (update: any, queryId: string) => {
    // Only process updates for current query
    // For follow-ups, we stream on a separate queryId but keep showing the original result.
    // Only ignore updates for different queries if this is an initial/new query flow.
    if (!messages.length && currentQueryId && queryId !== currentQueryId && currentQueryId !== '') {
      console.log('Ignoring update for different query during initial flow:', queryId, 'current:', currentQueryId);
      return;
    }
    console.log('Query update received:', {
      status: update.status,
      hasExecutionPlan: !!update.executionPlan,
      hasResult: !!update.result,
      hasStructuredResponse: !!update.structured_response,
      progress: update.progress,
      isDone: update.done
    });
    
    // If this is just a done signal, don't overwrite existing data
    if (update.done && !update.structured_response && !update.result && !update.status) {
      console.log('Received done signal, query processing complete');
      setIsProcessing(false);
      return;
    }
    
    // Update query progress with correct current agent
    console.log('=== CURRENT AGENT DETECTION ===');
    console.log('Raw currentAgent:', update.currentAgent);
    console.log('CurrentStep:', update.currentStep);
    
    // Parse agent from currentStep first (e.g., "Executing Financial Analyst...")
    let detectedAgent = 'Strategy Agent';
    if (update.currentStep) {
      if (update.currentStep.includes('Executing Financial Analyst') || update.currentStep.includes('Financial Analyst')) {
        detectedAgent = 'Financial Analyst';
      } else if (update.currentStep.includes('Executing Market Intelligence') || update.currentStep.includes('Market Intelligence')) {
        detectedAgent = 'Market Intelligence';
      } else if (update.currentStep.includes('Executing Risk Analyst') || update.currentStep.includes('Risk Analyst')) {
        detectedAgent = 'Risk Analyst';
      } else if (update.currentStep.includes('Executing Compliance Analyst') || update.currentStep.includes('Compliance Analyst')) {
        detectedAgent = 'Compliance Analyst';
      } else if (update.currentStep.includes('Executing Customer Insights') || update.currentStep.includes('Customer Insights') || update.currentStep.includes('CX Insights')) {
        detectedAgent = 'Customer Insights';
      } else if (update.currentStep.includes('Executing Technology Analyst') || update.currentStep.includes('Technology Analyst') || update.currentStep.includes('IT Analyst')) {
        detectedAgent = 'Technology Analyst';
      } else if (update.currentStep.includes('Synthesizing')) {
        detectedAgent = 'Strategy Agent';
      }
    }
    
    // Only use update.currentAgent if we couldn't detect from currentStep
    if (detectedAgent === 'Strategy Agent' && update.currentAgent && update.currentAgent !== 'Strategy Agent') {
      detectedAgent = update.currentAgent;
    }
    
    console.log('Detected agent:', detectedAgent);
    
    // Check if this is a follow-up query
    const isFollowupQuery = Boolean(
      update?.structured_response?.is_followup ||
      update?.is_followup ||
      update?.context?.is_followup ||
      update?.structured_response?.context?.is_followup
    );
    
    // Only show progress for non-follow-up queries
    if (!isFollowupQuery) {
      const progressUpdate = {
        ...update,
        currentAgent: detectedAgent
      };
      setQueryProgress(progressUpdate);
    } else {
      // Clear progress for follow-up queries
      setQueryProgress(null);
      // Also avoid adding extra system messages beyond the initial "Answering from the current analysis..."
      // e.g., do not add "Query completed" banners for follow-ups (handled below)
    }

    // Check for execution plan in multiple locations
    const executionPlan = update.executionPlan || 
                          (update.structured_response && update.structured_response.executionPlan) ||
                          null;
    // Never render execution plan for follow-up questions
    if (isFollowupQuery && executionPlan) {
      console.log('Follow-up query detected — suppressing execution plan rendering');
    }

    // Handle execution plan (pending_approval OR processing with executionPlan)
    // Check if we already have an execution plan message for this query
    const hasExistingPlanMessage = messages.some(msg => 
      msg.type === 'execution_plan' && msg.queryId === queryId
    );
    
    console.log('=== EXECUTION PLAN CHECK ===');
    console.log('Has execution plan?', !!executionPlan);
    console.log('Already has plan message?', hasExistingPlanMessage);
    console.log('Already shown for project (state)?', hasShownExecutionPlan[project.project_id]);
    console.log('Already shown for project (ref)?', shownExecutionPlansRef.current.has(project.project_id));
    console.log('Progress:', update.progress);
    console.log('Status:', update.status);
    
    // Only show execution plan if ALL conditions are met:
    // 1. We have an execution plan
    // 2. No existing plan message in chat
    // 3. Haven't shown for this project yet (check both ref and state)
    // 4. Status is EXACTLY pending_approval (not processing after approval)
    // 5. Progress is less than or equal to 50 (not after approval)
    if (executionPlan && !isFollowupQuery && !hasExistingPlanMessage && 
        !hasShownExecutionPlan[project.project_id] && !shownExecutionPlansRef.current.has(project.project_id) &&
        update.status === 'pending_approval' && update.progress <= 50) {
      console.log('SHOWING EXECUTION PLAN FOR APPROVAL');
      console.log('Execution plan:', executionPlan);
      console.log('Query status:', update.status);
      console.log('Current step:', update.currentStep);
      console.log('Progress:', update.progress);
      
      // Check if this is awaiting approval based on status
      const isAwaitingApproval = update.status === 'pending_approval';
      
      console.log('Is awaiting approval?', isAwaitingApproval);
      
      if (isAwaitingApproval) {
        console.log('ADDING EXECUTION PLAN MESSAGE TO CHAT');
        
        // IMMEDIATELY mark in the ref to prevent race conditions (use project ID)
        shownExecutionPlansRef.current.add(project.project_id);
        
        // Also update state for consistency
        setHasShownExecutionPlan(prev => {
          // Double-check we haven't already marked this project
          if (prev[project.project_id]) {
            console.log('Already marked as shown in state callback, skipping...');
            return prev;
          }
          return { ...prev, [project.project_id]: true };
        });
        setIsProcessing(false);
        
        // Add message only if we successfully updated the state
        setMessages(prev => {
          // Final check - make sure we don't already have an execution plan message for this project
          const alreadyHasPlan = prev.some(msg => 
            msg.type === 'execution_plan'
          );
          if (alreadyHasPlan) {
            console.log('Execution plan message already exists for this project, not adding duplicate');
            return prev;
          }
          const newMessage = {
            id: `${Date.now()}-${Math.random().toString(36).slice(2,8)}`,
            type: 'execution_plan' as const,
            content: 'Execution plan ready for your approval:',
            executionPlan: executionPlan,
            queryId: queryId,
            timestamp: new Date().toISOString()
          };
          console.log('📋 ADDING EXECUTION PLAN MESSAGE:', newMessage);
          console.log('📋 Current messages count:', prev.length);
          console.log('📋 New messages count will be:', prev.length + 1);
          const newMessages = [...prev, newMessage];
          console.log('📋 Messages after adding execution plan:', newMessages);
          return newMessages;
        });
        return; // Don't process further when awaiting approval
      }
    } else {
      console.log('NOT showing execution plan because:', {
        hasExecutionPlan: !!executionPlan,
        hasExistingPlanMessage,
        alreadyShown: hasShownExecutionPlan[project.project_id],
        progress: update.progress,
        progressCheck: update.progress <= 50
      });
    }
    
    // If we're processing after approval, make sure we're showing progress not execution plan
    if (update.status === 'processing' && update.progress >= 50) {
      // Query is actively processing after approval
      setIsProcessing(true);
      console.log('Query processing after approval, progress:', update.progress);
      
      // Check if this is actually complete but status not updated
      if (update.progress === 100 && update.endTime) {
        console.log('Query appears complete (100% with endTime) but status still processing');
        // Trigger completion handling
        update.status = 'completed';
      }
    }

    // Handle completion - check for endTime or 100% progress as well
    const isCompleted = update.status === 'completed' || 
                       (update.endTime && update.progress === 100) ||
                       (update.progress === 100 && update.currentStep?.toLowerCase().includes('complete')) ||
                       update.done;
    
    if (isCompleted) {
      console.log('🔍 Completion detection for queryId:', queryId, {
        status: update.status,
        progress: update.progress,
        endTime: update.endTime,
        done: update.done,
        currentStep: update.currentStep,
        hasShownCompletion: hasShownCompletion[queryId],
        shownCompletionsRef: shownCompletionsRef.current.has(queryId)
      });
      
      // Prevent duplicate processing - check both ref and state
      if (shownCompletionsRef.current.has(queryId) || hasShownCompletion[queryId]) {
        console.log('⚠️ Already processed completion for query:', queryId);
        return;
      }
      
      // Mark as processed immediately to prevent race conditions
      console.log('✅ Marking completion as processed for query:', queryId);
      shownCompletionsRef.current.add(queryId);
      setHasShownCompletion(prev => ({ ...prev, [queryId]: true }));
      
      // Extract the actual result from the update
      let finalResult: any = null;
      
      // Fetch the full result from the API since SSE stream doesn't contain the data
      try {
        console.log('📥 Fetching full results from API for queryId:', queryId);
        const fullResultResponse = await enhancedAPI.getQueryFullResult(queryId);
        
        // Check if this is a structured result (new format)
        if (fullResultResponse.areas || fullResultResponse.executive_summary || fullResultResponse.sections) {
          finalResult = fullResultResponse;  // Use entire response
          console.log('✅ Successfully fetched STRUCTURED results:', {
            hasAreas: !!fullResultResponse.areas,
            hasExecutiveSummary: !!fullResultResponse.executive_summary,
            areas: fullResultResponse.areas ? Object.keys(fullResultResponse.areas) : [],
            recommendations: fullResultResponse.recommendations ? fullResultResponse.recommendations.length : 0,
            agentResponses: fullResultResponse.agent_responses ? Object.keys(fullResultResponse.agent_responses).length : 0
          });
        } else {
          // Legacy format
          finalResult = fullResultResponse.structured_response || fullResultResponse.result;
          console.log('✅ Successfully fetched LEGACY results:', {
            hasStructuredResponse: !!fullResultResponse.structured_response,
            hasResult: !!fullResultResponse.result,
            areas: fullResultResponse.structured_response?.areas ? Object.keys(fullResultResponse.structured_response.areas) : []
          });
        }
      } catch (error) {
        console.error('❌ Failed to fetch full results, falling back to update data:', error);
        // Fallback to checking update data
        if (update.structured_response) {
          finalResult = update.structured_response;
        } else if (update.result) {
          finalResult = update.result;
        } else if (update.steps && update.steps.length > 0) {
          // Try to extract from the last step
          const lastStep = update.steps[update.steps.length - 1];
          if (lastStep.result) {
            finalResult = lastStep.result;
          }
          // Also check if the last step is synthesis and has structured data in reasoning
          if (!finalResult && lastStep.action?.includes('Synthesizing') && lastStep.reasoning) {
            try {
              // The synthesis result might be in the reasoning field as JSON
              const reasoningData = typeof lastStep.reasoning === 'string' 
                ? JSON.parse(lastStep.reasoning) 
                : lastStep.reasoning;
              if (reasoningData.executive_summary || reasoningData.areas) {
                finalResult = reasoningData;
                console.log('Extracted synthesis result from reasoning field');
              }
            } catch (e) {
              console.log('Could not parse synthesis reasoning as JSON');
            }
          }
        }
      }
      
      // If we have a result, display it
      if (finalResult) {
        // For follow-up answers, don't replace the right-side structured analysis
        const isFollowupAnswer = update?.structured_response?.is_followup || 
                                 finalResult?.is_followup || 
                                 false;
        
        // Parse if it's a JSON string
        if (typeof finalResult === 'string') {
          try {
            finalResult = JSON.parse(finalResult);
          } catch (e) {
            console.log('Result is not JSON, using as-is');
          }
        }
        
        // For follow-up queries, extract and display the answer
        if (isFollowupAnswer) {
          console.log('📝 Processing follow-up answer for queryId:', queryId);
          console.log('📝 finalResult structure:', {
            hasExecutiveSummary: !!finalResult?.executive_summary,
            executiveSummaryPreview: finalResult?.executive_summary?.substring(0, 100),
            hasFinalResponse: !!finalResult?.final_response,
            hasAnswer: !!finalResult?.answer,
            hasSummary: !!finalResult?.summary,
            isString: typeof finalResult === 'string'
          });
          
          // Extract the actual answer text from the response - check executive_summary first
          let followupAnswer = finalResult?.executive_summary || 
                               finalResult?.final_response || 
                               finalResult?.answer || 
                               finalResult?.summary ||
                               (typeof finalResult === 'string' ? finalResult : 
                                'Answer extracted from current analysis.');

          // Sanitize to ensure we show ONLY the direct answer (strip labels/bullets and keep first sentence/line)
          try {
            const firstLine = (followupAnswer || '').split(/\r?\n/)[0];
            const strippedLabel = firstLine.replace(/^\s*(?:\d+\.|-)?\s*(?:Direct Answer:)?\s*/i, '');
            // If multiple sentences, keep the first sentence
            const firstSentenceMatch = strippedLabel.match(/^[^.!?]*[.!?]/);
            const directOnly = (firstSentenceMatch ? firstSentenceMatch[0] : strippedLabel).trim();
            if (directOnly) {
              followupAnswer = directOnly;
            }
          } catch (_) {
            // best-effort; fall back to original text
          }
          
          console.log('📝 Extracted follow-up answer:', followupAnswer.substring(0, 200));
          
          // Add the actual answer to the chat - only once
          if (followupAnswer && followupAnswer !== 'Answer extracted from current analysis.' && !hasShownCompletion[queryId]) {
            // Use addMessage to ensure this gets saved to backend (User Story 11350)
            addMessage('assistant', followupAnswer, undefined, queryId);
            shownCompletionsRef.current.add(queryId);
            setHasShownCompletion(prev => ({ ...prev, [queryId]: true }));
          }
        } else {
          // For non-follow-up queries, update the right panel
          setQueryResult(finalResult);
          
          // Only show completion message once per query
          const hasCompletionInHistory = messages.some(msg => 
            msg.type === 'system' &&
            msg.content?.includes('Query completed') &&
            msg.queryId === queryId
          );
          
          if (!hasCompletionInHistory) {
            // Use addMessage to ensure this gets saved to backend (User Story 11350)
            console.log('📝 Adding completion message for query:', queryId);
            addMessage('system', '✅ Query completed. Results are displayed on the right.', undefined, queryId);
          } else {
            console.log('📝 Skipping completion message - already in history for query:', queryId);
          }
        }
        
        setIsProcessing(false);
        
        // Save result to local state for persistence
        if (currentQueryId) {
          setSavedResults(prev => ({
            ...prev,
            [currentQueryId]: finalResult
          }));
        }
        
        // The backend should handle storing the result via monitor_query_completion
        // We just need to refresh the project data after a delay to ensure it's saved
        if (currentQueryId && !isFollowupAnswer) {
          // Wait a bit for backend to save the result, then refresh
          setTimeout(async () => {
            try {
              const updatedProject = await enhancedAPI.getProject(project.project_id);
              if (updatedProject && updatedProject.conversation_history) {
                // Check if the result was saved
                const entry = updatedProject.conversation_history.find(
                  (e: ConversationEntry) => e.query_id === currentQueryId
                );
                if (entry && entry.result) {
                  console.log('✅ Backend successfully saved query result');
                  // Update local state with the saved result to ensure consistency
                  if (!finalResult && entry.result) {
                    // Handle QueryResult structure (sections) vs legacy structure
                    const resultToRender = entry.result.sections || entry.result.structured_response || entry.result;
                    setQueryResult(resultToRender);
                  }
                } else {
                  console.warn('⚠️ Result not yet saved by backend, will retry...');
                  // Retry after another delay
                  setTimeout(async () => {
                    const retryProject = await enhancedAPI.getProject(project.project_id);
                    const retryEntry = retryProject?.conversation_history?.find(
                      (e: ConversationEntry) => e.query_id === currentQueryId
                    );
                    if (retryEntry && retryEntry.result) {
                      console.log('✅ Backend saved query result on retry');
                      if (!finalResult && retryEntry.result) {
                        // Handle QueryResult structure (sections) vs legacy structure
                        const resultToRender = retryEntry.result.sections || retryEntry.result.structured_response || retryEntry.result;
                        setQueryResult(resultToRender);
                      }
                    }
                  }, 3000);
                }
              }
            } catch (error) {
              console.error('Failed to refresh project after query completion:', error);
            }
          }, 2000); // Wait 2 seconds for backend to save
        }
      } else {
        // No result found, show what we have
        console.log('No final result found in update, using raw update:', update);
        setQueryResult(update);
        setIsProcessing(false);
        // Don't add duplicate message here - it was already added above
      }
      
      // Don't reload conversation history if we have a good result
      // This prevents the flashing issue where the good result is overwritten
      console.log('Query completed, keeping current result display');
    }

    // Handle failure
    if (update.status === 'failed') {
      setIsProcessing(false);
      addMessage('system', `❌ Query failed: ${update.error || 'Unknown error'}. Please try again.`);
    }
  };

  const addMessage = (type: Message['type'], content: string, executionPlan?: any, queryId?: string) => {
    // Skip adding messages to chat during sync mode
    if (isSyncMode) {
      console.log('🔕 Skipping message during sync mode:', content);
      return;
    }
    
    const message: Message = {
      id: `msg-${Date.now()}`,
      type,
      content,
      timestamp: new Date().toISOString(),
      executionPlan,
      queryId
    };
    setMessages(prev => [...prev, message]);
    
    // Save all messages to backend for persistence (User Story 11350) - Non-blocking
    if (currentEntryId && (type === 'system' || type === 'assistant' || type === 'processing' || type === 'user')) {
      enhancedAPI.saveChatMessage(
        project.project_id,
        currentEntryId,
        type,
        content,
        queryId || currentQueryId || undefined,
        { executionPlan }
      ).then(() => {
        console.log('💾 Saved chat message to backend:', type, content.slice(0, 50) + '...');
      }).catch(error => {
        console.error('Failed to save chat message to backend:', error);
        // Don't fail the UI if backend save fails
      });
    }
  };

  const handleSyncProject = async () => {
    // Enable sync mode to suppress chat messages
    setIsSyncMode(true);
    console.log('🔄 Starting sync in silent mode');
    
    // Manual sync - refresh the project data first
    try {
      // First, get the latest project data
      const latestProject = await enhancedAPI.getProject(project.project_id);
      
      // Check if we have valid results already
      if (latestProject.conversation_history?.length > 0) {
        const lastEntry = latestProject.conversation_history[latestProject.conversation_history.length - 1];
        if (lastEntry.result && lastEntry.result.status === 'completed') {
          // We have results, just reload them
          loadConversationHistory(latestProject);
          addMessage('system', '✅ Project data refreshed from server');
          return;
        }
      }
      
      // No valid results, re-run the initial query
      if (!project.initial_query) {
        addMessage('system', '⚠️ No initial query to sync');
        setIsSyncMode(false);
        return;
      }
      
      if (isProcessing) {
        addMessage('system', '⚠️ A query is already being processed');
        setIsSyncMode(false);
        return;
      }
      
      // Clear existing messages (except execution plans) but don't add sync notification during sync mode
      setMessages(prev => prev.filter(msg => msg.type === 'execution_plan'));
    } catch (error) {
      console.error('Failed to refresh project:', error);
      addMessage('system', '❌ Failed to refresh project. Please try again.');
      setIsSyncMode(false);
      return;
    }
    
    // Submit the initial query as a fresh query
    try {
      setIsProcessing(true);
      setWaitingForApproval(false);
      
      const response = await enhancedAPI.submitQuery(
        project.initial_query,
        {
          project_id: project.project_id,
          project_name: project.name,
          session_id: project.project_id,
          require_approval: false,  // Auto-execute for sync operations
          uploaded_files: uploadedFiles.map(f => ({
            file_id: f.file_id,
            filename: f.filename,
            file_type: (f as any).file_type || 'document'
          })),
          is_sync: true // Mark this as a sync operation
        }
      );
      
      if (response && response.queryId) {
        setCurrentQueryId(response.queryId);
        console.log(`🔄 Sync query submitted silently. Query ID: ${response.queryId}`);
        
        // Start SSE streaming for the new query
        // The enhancedAPI will handle closing existing connections
        const sse = enhancedAPI.streamQueryStatus(response.queryId, (update) => {
          console.log('SSE update from sync:', update);
          handleQueryUpdate(update, response.queryId);
          
          // Exit sync mode when query completes
          if (update.status === 'completed' || update.status === 'failed' || update.done) {
            console.log('🔄 Sync completed, exiting sync mode');
            setIsSyncMode(false);
          }
        });
        
        setEventSource(sse);
        
        sse.addEventListener('done', () => {
          console.log('🔄 Sync stream ended, exiting sync mode');
          setIsSyncMode(false);
        });
      } else {
        setIsSyncMode(false);
      }
    } catch (error) {
      console.error('Failed to sync project:', error);
      addMessage('system', '❌ Failed to sync project. Please try again.');
      setIsProcessing(false);
      setIsSyncMode(false);
    }
  };

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || isProcessing) {
      console.log('🚫 Message sending blocked:', { message: inputMessage.trim(), isProcessing });
      return;
    }

    const userMessage = inputMessage.trim();
    setInputMessage('');
    addMessage('user', userMessage);
    setIsProcessing(true);
    setQueryProgress(null);
    // Do not clear the right-side result for follow-up questions
    // A follow-up is when we have messages AND a query result already displayed
    // Just having messages from loading history doesn't make it a follow-up
    const isFollowupPre = messages.length > 0 && !!currentQueryId && !!queryResult;
    
    console.log('🚀 Starting message send:', { message: userMessage, isFollowup: isFollowupPre });
    // Don't clear query result for follow-ups - this was causing the white screen issue
    if (!isFollowupPre) {
      // Only clear for new queries, not follow-ups
      setQueryResult(null);
    }
    // Don't reset hasShownCompletion here - it's tracked per query ID now
    setHasShownExecutionPlan({}); // Reset execution plan flags for new query
    
    // Add processing confirmation (different text for follow-ups)
    addMessage('system', isFollowupPre ? '💬 Answering from the current analysis...' : '🔍 Processing your request...');

    try {
      const isFollowup = isFollowupPre;

      if (isFollowup && currentQueryId) {
        // Use dedicated follow-up endpoint to ensure context is preserved
        const followup = await enhancedAPI.submitFollowUpQuery(currentQueryId, userMessage);
        const followupQueryId = followup.queryId;
        // Keep showing the existing result; do not switch currentQueryId to avoid clearing the panel
        // We still want to receive and display the short answer in chat once streaming completes
        // The enhancedAPI will handle closing existing connections
        const sse = enhancedAPI.streamQueryStatus(followupQueryId, (update) => {
          // Only update chat/progress state; do not override the right-side structured result
          handleQueryUpdate(update, followupQueryId);
        });
        setEventSource(sse);
      } else {
        // Submit as a new project query
        console.log('📤 Submitting project query from chat:', { projectId: project.project_id, message: userMessage });
        const result = await enhancedAPI.submitProjectQuery(
          project.project_id,
          userMessage,
          false,
          undefined
        );
        console.log('📥 Project query response from chat:', result);
        
        // Track the conversation entry ID for chat message persistence (User Story 11350)
        if (result.entry_id) {
          setCurrentEntryId(result.entry_id);
          console.log('🔗 Set current entry ID for user query:', result.entry_id);
        }
        const queryId = result.query_id || result.queryId || result.id;
        setCurrentQueryId(queryId);

        if (eventSource) {
          eventSource.close();
        }
        const sse = enhancedAPI.streamQueryStatus(queryId, (update) => {
          handleQueryUpdate(update, queryId);
        });
        setEventSource(sse);
      }
    } catch (error) {
      console.error('Failed to send message:', error);
      addMessage('system', '❌ Failed to process query. Please try again.');
      setIsProcessing(false);
    }
  };

  const handleApproveExecution = async (queryId: string) => {
    try {
      // First mark the message as action taken to prevent double clicks
      setMessages(prev => prev.map(msg => 
        msg.type === 'execution_plan' && msg.queryId === queryId 
          ? { ...msg, actionTaken: true }
          : msg
      ));
      
      // Check current query status
      const currentStatus = await enhancedAPI.getQueryStatus(queryId);
      console.log('Current query status before approval:', currentStatus);
      
      if (currentStatus.status === 'completed') {
        addMessage('system', 'ℹ️ This query has already been executed. Results are shown on the right.');
        return;
      }
      
      // Allow approval if:
      // 1. Status is pending_approval OR
      // 2. Status is processing AND currentStep indicates awaiting approval AND progress < 50
      const isAwaitingApproval = 
        currentStatus.status === 'pending_approval' ||
        (currentStatus.status === 'processing' && 
         currentStatus.currentStep?.includes('Awaiting approval') &&
         currentStatus.progress < 50);
      
      if (!isAwaitingApproval) {
        // Check if already processing after approval
        if (currentStatus.status === 'processing' && currentStatus.progress >= 50) {
          addMessage('system', 'ℹ️ This execution plan has already been approved and is being processed.');
          return;
        }
        addMessage('system', `ℹ️ Query is in ${currentStatus.status} state and cannot be approved.`);
        return;
      }
      
      await enhancedAPI.approveExecutionPlan(queryId);
      addMessage('system', '🚀 Execution plan approved. Processing your query...');
      setIsProcessing(true);
      
      // Mark the execution plan message as action taken (hide buttons but keep plan)
      setMessages(prev => prev.map(msg => 
        msg.type === 'execution_plan' && msg.queryId === queryId 
          ? { ...msg, actionTaken: true }
          : msg
      ));
      
      // Ensure we don't show the execution plan again for this query
      shownExecutionPlansRef.current.add(queryId);
      setHasShownExecutionPlan(prev => ({ ...prev, [queryId]: true }));
      
      // Don't start a new SSE stream - the existing one should continue
      // The backend should automatically continue processing after approval
      console.log('Approval sent, waiting for existing SSE stream to continue...');
    } catch (error: any) {
      console.error('Failed to approve execution:', error);
      if (error.response?.status === 400) {
        const errorDetail = error.response?.data?.detail || '';
        if (errorDetail.includes('already approved') || errorDetail.includes('already processed')) {
          addMessage('system', 'ℹ️ This execution plan has already been approved.');
          // Mark the execution plan message as action taken
          setMessages(prev => prev.map(msg => 
            msg.type === 'execution_plan' && msg.queryId === queryId 
              ? { ...msg, actionTaken: true }
              : msg
          ));
        } else {
          addMessage('system', `ℹ️ ${errorDetail || 'Cannot approve at this time.'}`);
        }
      } else {
        addMessage('system', '❌ Failed to approve execution plan. Please try again.');
      }
    }
  };

  const [pdfExportType, setPdfExportType] = useState<'fab' | 'columbus'>('fab');

  const handleExportPDF = async (result: any, exportType: 'fab' | 'columbus' = 'fab') => {
    // Show the PDF export dialog for section selection
    setPdfExportType(exportType);
    setShowPDFDialog(true);
  };

  const handlePDFExportWithOptions = async (options: PDFExportOptions) => {
    setShowPDFDialog(false);
    // Use enhanced PDF generation with selected options
    if (options.sections.length === 0) {
      addMessage('system', '⚠️ No sections selected for export');
      return;
    }
    console.log(`Generating ${pdfExportType.toUpperCase()} PDF with options:`, options);
    
    // Try server-side generation based on export type
    try {
      addMessage('system', `⏳ Generating ${pdfExportType === 'columbus' ? 'Columbus professional' : 'FAB branded'} PDF report...`);
      
      const result = pdfExportType === 'columbus' 
        ? await enhancedAPI.generateColumbusPDF(queryResult, options)
        : await enhancedAPI.generateFABPDF(queryResult, options);
      
      if (result && result.pdf_base64) {
        // Download the PDF
        const link = document.createElement('a');
        link.href = `data:application/pdf;base64,${result.pdf_base64}`;
        link.download = result.filename || `${pdfExportType}_report_${currentQueryId}_${new Date().toISOString().split('T')[0]}.pdf`;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        
        addMessage('system', `✅ ${pdfExportType === 'columbus' ? 'Columbus' : 'FAB'} PDF report generated successfully!`);
        return;
      }
    } catch (error) {
      console.log(`${pdfExportType} PDF generation failed, falling back to client-side:`, error);
    }
    
    // Fallback to client-side generation
    await handleExportPDFClient(queryResult, options);
  };

  const handleExportPDFClient = async (result: any, options?: PDFExportOptions) => {
    try {
      addMessage('system', '⏳ Generating PDF report...');
      
      const pdf = new jsPDF('p', 'mm', 'a4');
      const pageWidth = pdf.internal.pageSize.getWidth();
      const pageHeight = pdf.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - 2 * margin;
      let yPosition = margin;
      
      // Title page with professional branding
      pdf.setFontSize(28);
      pdf.setTextColor(30, 58, 95); // Professional dark blue
      pdf.text('Strategic Analysis Report', pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 15;
      
      pdf.setFontSize(12);
      pdf.setTextColor(100, 100, 100);
      pdf.text(`Generated: ${new Date().toLocaleDateString()}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 10;
      pdf.text(`Query ID: ${currentQueryId}`, pageWidth / 2, yPosition, { align: 'center' });
      yPosition += 25;
      
      // Executive Summary section (following SCR structure implicitly)
      if (result.executive_summary) {
        pdf.setFontSize(18);
        pdf.setTextColor(30, 58, 95);
        pdf.text('Executive Summary', margin, yPosition);
        yPosition += 10;
        
        pdf.setFontSize(11);
        pdf.setTextColor(0, 0, 0);
        const summaryLines = pdf.splitTextToSize(result.executive_summary, contentWidth);
        summaryLines.forEach((line: string) => {
          if (yPosition > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(line, margin, yPosition);
          yPosition += 6;
        });
        yPosition += 15;
      }

      // SWOT Analysis section
      if (result.swot_analysis) {
        if (yPosition > pageHeight - 100) {
          pdf.addPage();
          yPosition = margin;
        }
        
        pdf.setFontSize(18);
        pdf.setTextColor(30, 58, 95);
        pdf.text('SWOT Analysis', margin, yPosition);
        yPosition += 15;
        
        const swotData = result.swot_analysis;
        const swotSections = [
          { title: 'Strengths', data: swotData.strengths, color: [34, 139, 34] },
          { title: 'Weaknesses', data: swotData.weaknesses, color: [220, 20, 60] },
          { title: 'Opportunities', data: swotData.opportunities, color: [30, 144, 255] },
          { title: 'Threats', data: swotData.threats, color: [255, 140, 0] }
        ];
        
        swotSections.forEach(section => {
          if (section.data && section.data.length > 0) {
            // Section title
            pdf.setFontSize(12);
            pdf.setTextColor(section.color[0], section.color[1], section.color[2]);
            pdf.text(section.title, margin, yPosition);
            yPosition += 8;
            
            // Section items
            pdf.setFontSize(10);
            pdf.setTextColor(0, 0, 0);
            section.data.forEach((item: string) => {
              if (yPosition > pageHeight - margin) {
                pdf.addPage();
                yPosition = margin;
              }
              const itemLines = pdf.splitTextToSize(`• ${item}`, contentWidth - 5);
              itemLines.forEach((line: string) => {
                pdf.text(line, margin + 5, yPosition);
                yPosition += 5;
              });
            });
            yPosition += 5;
          }
        });
        
        yPosition += 10;
      }
      
      // Key Insights section (Complication phase implicitly)
      pdf.setFontSize(18);
      pdf.setTextColor(30, 58, 95);
      pdf.text('Key Analysis & Insights', margin, yPosition);
      yPosition += 15;
      pdf.setTextColor(0, 0, 0);
      
      // Process each area
      if (result.areas) {
        for (const [areaName, areaData] of Object.entries(result.areas)) {
          const area = areaData as any;
          
          // Check if we need a new page
          if (yPosition > pageHeight - 50) {
            pdf.addPage();
            yPosition = margin;
          }
          
          // Area title
          pdf.setFontSize(14);
          pdf.text(areaName.replace(/_/g, ' ').toUpperCase(), margin, yPosition);
          yPosition += 10;
          
          // Analysis
          if (area.analysis) {
            pdf.setFontSize(11);
            const analysisLines = pdf.splitTextToSize(area.analysis, contentWidth);
            analysisLines.forEach((line: string) => {
              if (yPosition > pageHeight - margin) {
                pdf.addPage();
                yPosition = margin;
              }
              pdf.text(line, margin, yPosition);
              yPosition += 6;
            });
            yPosition += 5;
          }

          // Key Metrics (if any)
          if ((area.key_metrics || area.kpis) && (area.key_metrics || area.kpis).length > 0) {
            if (yPosition > pageHeight - 40) {
              pdf.addPage();
              yPosition = margin;
            }
            pdf.setFontSize(12);
            pdf.text('Key Metrics', margin, yPosition);
            yPosition += 8;
            pdf.setFontSize(10);
            (area.key_metrics || area.kpis).forEach((kpi: any) => {
              const line = `${kpi.label ?? ''}: ${kpi.value ?? ''}` +
                (kpi.change ? ` (${kpi.change}${kpi.trend ? `, ${kpi.trend}` : ''})` : '');
              if (yPosition > pageHeight - margin) {
                pdf.addPage();
                yPosition = margin;
              }
              pdf.text(`• ${line}`, margin + 5, yPosition);
              yPosition += 5;
            });
            yPosition += 6;
          }
          
          // Key findings
          if (area.key_findings && area.key_findings.length > 0) {
            pdf.setFontSize(12);
            pdf.text('Key Findings:', margin, yPosition);
            yPosition += 8;
            
            pdf.setFontSize(10);
            area.key_findings.forEach((finding: string) => {
              if (yPosition > pageHeight - margin) {
                pdf.addPage();
                yPosition = margin;
              }
              const findingLines = pdf.splitTextToSize(`• ${finding}`, contentWidth - 5);
              findingLines.forEach((line: string) => {
                pdf.text(line, margin + 5, yPosition);
                yPosition += 5;
              });
            });
            yPosition += 5;
          }
          
          // Charts - Direct rendering from data instead of DOM capture
          if (area.charts && area.charts.length > 0) {
            if (yPosition > pageHeight - 40) {
              pdf.addPage();
              yPosition = margin;
            }
            
            pdf.setFontSize(12);
            pdf.setTextColor(44, 82, 130);
            pdf.text('Visualizations', margin, yPosition);
            yPosition += 8;
            
            for (let i = 0; i < area.charts.length; i++) {
              if (yPosition > pageHeight - 80) {
                pdf.addPage();
                yPosition = margin;
              }
              
              const chart = area.charts[i];
              const chartTitle = chart.chart_title || chart.title || `Chart ${i + 1}`;
              const chartType = chart.chart_type || chart.type || 'bar';
              
              // Add chart title
              pdf.setFontSize(11);
              pdf.setTextColor(44, 82, 130);
              pdf.text(chartTitle, margin, yPosition);
              yPosition += 6;
              
              // Render chart data as a summary table
              pdf.setFontSize(9);
              pdf.setTextColor(60, 60, 60);
              
              if (chart.datasets && chart.labels) {
                // Structured chart data
                const tableData: string[][] = [];
                tableData.push(['Label', ...chart.datasets.map((ds: any) => ds.label)]);
                
                // Add data rows
                chart.labels.forEach((label: string, idx: number) => {
                  const row = [label];
                  chart.datasets.forEach((ds: any) => {
                    const value = ds.data[idx];
                    row.push(typeof value === 'number' ? value.toFixed(1) : String(value));
                  });
                  tableData.push(row);
                });
                
                // Render simple table
                const colWidth = contentWidth / tableData[0].length;
                tableData.forEach((row, rowIdx) => {
                  if (yPosition > pageHeight - 10) {
                    pdf.addPage();
                    yPosition = margin;
                  }
                  
                  row.forEach((cell, colIdx) => {
                    const x = margin + (colIdx * colWidth);
                    if (rowIdx === 0) {
                      pdf.setFontSize(9);
                      pdf.setTextColor(30, 58, 95);
                      pdf.setFont('helvetica', 'bold');
                    } else {
                      pdf.setFontSize(8);
                      pdf.setTextColor(60, 60, 60);
                      pdf.setFont('helvetica', 'normal');
                    }
                    pdf.text(cell.substring(0, 15), x, yPosition);
                  });
                  yPosition += 5;
                });
                
              } else if (chart.data) {
                // Simple data array
                pdf.text(`Type: ${chartType}`, margin + 5, yPosition);
                yPosition += 5;
                
                if (Array.isArray(chart.data)) {
                  const maxItems = Math.min(chart.data.length, 5);
                  for (let j = 0; j < maxItems; j++) {
                    if (yPosition > pageHeight - 10) {
                      pdf.addPage();
                      yPosition = margin;
                    }
                    const item = chart.data[j];
                    if (typeof item === 'object') {
                      const text = Object.entries(item).map(([k, v]) => `${k}: ${v}`).join(', ');
                      pdf.text(`• ${text.substring(0, 80)}`, margin + 5, yPosition);
                    } else {
                      pdf.text(`• ${String(item).substring(0, 80)}`, margin + 5, yPosition);
                    }
                    yPosition += 4;
                  }
                  if (chart.data.length > 5) {
                    pdf.text(`... and ${chart.data.length - 5} more items`, margin + 5, yPosition);
                    yPosition += 4;
                  }
                }
              } else {
                // No data available
                pdf.text(`Chart type: ${chartType}`, margin + 5, yPosition);
                yPosition += 5;
              }
              
              yPosition += 8;
              pdf.setTextColor(0, 0, 0);
              pdf.setFont('helvetica', 'normal');
            }
          }
          
          yPosition += 10;
        }
      }
      
      // Strategic Recommendations section (Resolution phase implicitly)
      if (result.recommendations && result.recommendations.length > 0 && (!options || options.includeRecommendations)) {
        if (yPosition > pageHeight - 50) {
          pdf.addPage();
          yPosition = margin;
        }
        
        pdf.setFontSize(18);
        pdf.setTextColor(30, 58, 95);
        pdf.text('Strategic Recommendations', margin, yPosition);
        yPosition += 12;
        
        pdf.setFontSize(11);
        pdf.setTextColor(60, 60, 60);
        pdf.text('Based on our comprehensive analysis, we recommend:', margin, yPosition);
        yPosition += 10;
        
        pdf.setTextColor(0, 0, 0);
        pdf.setFontSize(10);
        result.recommendations.forEach((rec: any, index: number) => {
          if (yPosition > pageHeight - 30) {
            pdf.addPage();
            yPosition = margin;
          }
          
          // Strategic Initiative heading
          pdf.setFontSize(11);
          pdf.setTextColor(44, 82, 130);
          pdf.text(`Strategic Initiative ${index + 1}:`, margin, yPosition);
          yPosition += 6;
          
          // Recommendation content
          pdf.setTextColor(0, 0, 0);
          const title = typeof rec === 'string' ? rec : (rec.title || rec.recommendation || `Recommendation ${index + 1}`);
          const titleLines = pdf.splitTextToSize(title, contentWidth - 5);
          titleLines.forEach((line: string) => {
            pdf.text(line, margin + 5, yPosition);
            yPosition += 6;
          });
          
          // Recommendation details if available
          if (typeof rec === 'object' && rec.description) {
            pdf.setFontSize(10);
            const descLines = pdf.splitTextToSize(rec.description, contentWidth - 5);
            descLines.forEach((line: string) => {
              if (yPosition > pageHeight - margin) {
                pdf.addPage();
                yPosition = margin;
              }
              pdf.text(line, margin + 5, yPosition);
              yPosition += 5;
            });
          }
          
          yPosition += 8;
        });
        
        // Add Implementation Roadmap
        if (yPosition > pageHeight - 60) {
          pdf.addPage();
          yPosition = margin;
        }
        
        yPosition += 10;
        pdf.setFontSize(14);
        pdf.setTextColor(30, 58, 95);
        pdf.text('Implementation Roadmap', margin, yPosition);
        yPosition += 8;
        
        pdf.setFontSize(10);
        pdf.setTextColor(0, 0, 0);
        const roadmap = [
          'Phase 1 (0-3 months): Foundation and quick wins',
          'Phase 2 (3-6 months): Core capability development',
          'Phase 3 (6-12 months): Strategic transformation',
          'Phase 4 (12+ months): Continuous optimization and scaling'
        ];
        
        roadmap.forEach(phase => {
          if (yPosition > pageHeight - margin) {
            pdf.addPage();
            yPosition = margin;
          }
          pdf.text(`• ${phase}`, margin + 5, yPosition);
          yPosition += 6;
        });
        
        // Reset text color for any subsequent content
        pdf.setTextColor(0, 0, 0);
      }
      
      // Save the PDF
      pdf.save(`report_${currentQueryId}_${new Date().toISOString().split('T')[0]}.pdf`);
      addMessage('system', '📄 PDF report exported successfully');
      
    } catch (error) {
      console.error('Export PDF error:', error);
      addMessage('system', '❌ Failed to export PDF report');
    }
  };

  const handleExportPPTX = async (result: any) => {
    try {
      addMessage('system', '⏳ Generating PowerPoint presentation...');
      
      // Prepare query data for PPTX generation
      const queryData = {
        queryId: currentQueryId || 'unknown',
        query: result.query || 'Strategic Analysis',
        status: 'completed',
        executive_summary: result.executive_summary || '',
        charts: extractChartsFromResult(result),
        structured_response: {
          executive_summary: result.executive_summary || '',
          areas: result.areas || {},
          recommendations: result.recommendations || [],
          next_steps: result.next_steps || []
        }
      };
      
      // Call PPTX generation API
      // Use runtime config if available, fallback to env vars or defaults
      const getConfig = () => {
        if (typeof window !== 'undefined' && (window as any).RUNTIME_CONFIG) {
          return (window as any).RUNTIME_CONFIG;
        }
        return {
          API_URL: process.env.REACT_APP_API_URL || 'http://api.fab-agents.columbus.tech'
        };
      };
      const config = getConfig();
      const apiUrl = config.API_URL || process.env.REACT_APP_API_URL || 'http://localhost:8000';
      const response = await fetch(`${apiUrl}/api/v1/pptx/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          query_data: queryData,
          options: {
            include_charts: true,
            organizationName: 'FAB',
            replaceOrganizationNames: false
          }
        })
      });
      
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const data = await response.json();
      
      if (data.success && data.pptx_base64) {
        // Decode base64 and create blob
        const binaryString = atob(data.pptx_base64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        const blob = new Blob([bytes], { type: 'application/vnd.openxmlformats-officedocument.presentationml.presentation' });
        
        // Download the file
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = data.filename || `presentation_${currentQueryId}_${new Date().toISOString().split('T')[0]}.pptx`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        window.URL.revokeObjectURL(url);
        
        addMessage('system', '✅ PowerPoint presentation exported successfully');
      } else {
        throw new Error('Invalid response from server');
      }
    } catch (error) {
      console.error('Export PPTX error:', error);
      addMessage('system', '❌ Failed to export PowerPoint presentation');
    }
  };

  const extractChartsFromResult = (result: any) => {
    const charts: any[] = [];
    if (result.areas) {
      Object.values(result.areas).forEach((area: any) => {
        if (area.charts) {
          charts.push(...area.charts);
        }
      });
    }
    return charts;
  };

  const handleRejectExecution = async (queryId: string) => {
    try {
      await enhancedAPI.rejectExecutionPlan(queryId, 'User requested changes');
      addMessage('system', '🔄 Execution plan rejected. Please refine your query.');
      
      // Mark the execution plan message as action taken (hide buttons but keep plan)
      setMessages(prev => prev.map(msg => 
        msg.type === 'execution_plan' && msg.queryId === queryId 
          ? { ...msg, actionTaken: true }
          : msg
      ));
      
      if (eventSource) {
        eventSource.close();
      }
    } catch (error) {
      console.error('Failed to reject execution:', error);
    }
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;

    for (const file of Array.from(files)) {
      try {
        addMessage('system', `📎 Uploading file: ${file.name}...`);
        
        // Use the improved upload method that processes files automatically
        const metadata = await enhancedAPI.uploadProjectFile(
          project.project_id,
          file,
          'document'
        );
        
        setUploadedFiles(prev => [...prev, metadata]);
        addMessage('system', `✅ File uploaded and processed: ${file.name}`);
        
        // Refresh project files to ensure they're available for queries
        try {
          const projectFiles = await enhancedAPI.listProjectFiles(project.project_id);
          setUploadedFiles(projectFiles);
        } catch (error) {
          console.warn('Failed to refresh project files:', error);
        }
        
      } catch (error) {
        console.error('File upload failed:', error);
        addMessage('system', `❌ Failed to upload: ${file.name}`);
      }
    }

    // Clear file input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await enhancedAPI.deleteFile(fileId);
      setUploadedFiles(prev => prev.filter(f => f.file_id !== fileId));
      addMessage('system', '🗑️ File deleted');
    } catch (error) {
      console.error('Failed to delete file:', error);
      addMessage('system', '❌ Failed to delete file');
    }
  };

  const formatContent = (content: string) => {
    // Split by newlines and format
    return content.split('\n').map((line, idx) => {
      // Check for headers (lines starting with # or lines that are all caps)
      if (line.startsWith('#') || (line.length > 0 && line === line.toUpperCase())) {
        return <h5 key={idx}>{line.replace(/^#+\s*/, '')}</h5>;
      }
      // Check for bullet points
      if (line.trim().startsWith('•') || line.trim().startsWith('-') || line.trim().startsWith('*')) {
        return <li key={idx}>{line.trim().substring(1).trim()}</li>;
      }
      // Check for numbered lists
      if (/^\d+\./.test(line.trim())) {
        return <li key={idx}>{line.trim().substring(line.indexOf('.') + 1).trim()}</li>;
      }
      // Regular paragraph
      if (line.trim().length > 0) {
        return <p key={idx}>{line}</p>;
      }
      return null;
    }).filter(el => el !== null);
  };

  const renderQueryResult = (result: any) => {
    if (!result) return null;

    console.log('=== RENDERING QUERY RESULT ===');
    console.log('Result type:', typeof result);
    console.log('Result keys:', Object.keys(result));
    console.log('Has areas?', !!result.areas);
    console.log('Number of areas:', result.areas ? Object.keys(result.areas).length : 0);
    console.log('Has structured_response?', !!result.structured_response);
    console.log('Has executive_summary?', !!result.executive_summary);
    if (result.areas && typeof result.areas === 'object') {
      const areaNames = Object.keys(result.areas);
      console.log('[Render] area names:', areaNames);
      if (areaNames.length > 0) {
        const sample = areaNames[0];
        console.log('[Render] sample area', sample, 'keys:', Object.keys(result.areas[sample] || {}));
      }
    }
    if (result.sections) {
      const s = result.sections;
      console.log('[Render] sections present. type:', typeof s, 'keys:', typeof s === 'object' ? Object.keys(s) : '(n/a)');
      if (typeof s === 'object' && s.areas) {
        console.log('[Render] sections.areas keys:', Object.keys(s.areas || {}));
      }
    }

    // Ignore done-only updates that don't contain actual results
    if (result.done && !result.structured_response && !result.executive_summary && !result.areas && !result.final_response) {
      console.log('Ignoring done-only update');
      return null; // Don't render anything for done-only updates
    }

    // Check if it's a structured response with areas OR sections
    if (result.areas && Object.keys(result.areas).length > 0) {
      console.log('RENDERING AS STRUCTURED RESULT VIEW (areas)');
      return <StructuredResultView result={result} />;
    } else if (result.sections && (typeof result.sections === 'object' || typeof result.sections === 'string')) {
      // Handle old format with sections - convert to areas format
      console.log('Converting sections to areas format');
      let sections: any = result.sections;
      if (typeof sections === 'string') {
        try { sections = JSON.parse(sections); } catch (e) { console.warn('[Render] failed to parse sections string'); sections = {}; }
      }
      const areas = sections.areas && typeof sections.areas === 'object' ? sections.areas : sections;
      console.log('[Render] areas keys from sections:', Object.keys(areas || {}));
      const convertedResult = {
        ...result,
        areas,
        executive_summary: sections.executive_summary || result.executive_summary || result.summary || ''
      };
      console.log('[Render] converted result keys:', Object.keys(convertedResult), 'areaCount:', Object.keys(convertedResult.areas || {}).length, 'exec len:', (convertedResult.executive_summary || '').length);
      return <StructuredResultView result={convertedResult} />;
    }
    
    // Fallback for other response formats
    if (result.executive_summary || result.summary || result.final_response) {
      return (
        <div className="structured-result">
          {result.executive_summary && (
            <div className="result-section">
              <h5>Executive Summary</h5>
              <p>{result.executive_summary}</p>
            </div>
          )}
          
          {result.summary && (
            <div className="result-section">
              <h5>Detailed Analysis</h5>
              <p>{result.summary}</p>
            </div>
          )}
          
          {result.final_response && (
            <div className="result-section">
              <h5>Analysis Result</h5>
              <div className="formatted-content">{formatContent(result.final_response)}</div>
            </div>
          )}
          
          {result.areas && Object.entries(result.areas).map(([area, data]: [string, any]) => (
            (data.content || data.summary) && (
              <div key={area} className="result-area">
                <h5>{area.replace(/_/g, ' ').replace(/cx insights/gi, 'Customer Insights').replace(/CX Insights/gi, 'Customer Insights')}</h5>
                <p>{data.content || data.summary}</p>
                {data.highlights && data.highlights.length > 0 && (
                  <ul>
                    {data.highlights.map((highlight: string, idx: number) => (
                      <li key={idx}>{highlight}</li>
                    ))}
                  </ul>
                )}
              </div>
            )
          ))}
          
          {result.recommendations && result.recommendations.length > 0 && (
            <div className="result-section">
              <h5>Recommendations</h5>
              <ul>
                {result.recommendations.map((rec: any, idx: number) => (
                  <li key={idx}>
                    {typeof rec === 'string' ? rec : rec.description || rec.title || JSON.stringify(rec)}
                  </li>
                ))}
              </ul>
            </div>
          )}
          
          {result.risks && result.risks.length > 0 && (
            <div className="result-section">
              <h5>Risks</h5>
              <ul>
                {result.risks.map((risk: string, idx: number) => (
                  <li key={idx}>{risk}</li>
                ))}
              </ul>
            </div>
          )}
          
          {result.next_steps && result.next_steps.length > 0 && (
            <div className="result-section">
              <h5>Next Steps</h5>
              <ol>
                {result.next_steps.map((step: string, idx: number) => (
                  <li key={idx}>{step}</li>
                ))}
              </ol>
            </div>
          )}
        </div>
      );
    }
    
    // Fallback to JSON display
    return (
      <div className="result-content">
        <pre>{JSON.stringify(result, null, 2)}</pre>
      </div>
    );
  };

  const renderMessage = (message: Message) => {
    if (message.type === 'execution_plan' && message.executionPlan) {
      return (
        <div className="execution-plan-message">
          <h4>Execution Plan</h4>
          <div className="plan-content">
            <div className="plan-summary">
              <h5>Summary:</h5>
              <ul>
                {message.executionPlan.summary?.map((item: string, idx: number) => (
                  <li key={idx}>{item}</li>
                ))}
              </ul>
            </div>
            
            {message.executionPlan.agents?.length > 0 && (
              <div className="plan-agents">
                <h5>Agents to be involved:</h5>
                <div className="agent-list">
                  {message.executionPlan.agents.map((agent: string, idx: number) => (
                    <span key={idx} className="agent-badge">{agent}</span>
                  ))}
                </div>
              </div>
            )}
            
            <div className="plan-time">
              <p>Estimated time: {message.executionPlan.estimatedTime || '5-10 minutes'}</p>
            </div>
          </div>
          
          {!message.actionTaken && (
            <div className="plan-actions">
              <button 
                className="approve-btn"
                onClick={() => handleApproveExecution(message.queryId!)}
              >
                Accept & Proceed
              </button>
              <button 
                className="reject-btn"
                onClick={() => handleRejectExecution(message.queryId!)}
              >
                Modify Query
              </button>
            </div>
          )}
          
          {message.actionTaken && (
            <div className="plan-status">
              <p className="action-taken-note">✓ Action taken - Processing...</p>
            </div>
          )}
        </div>
      );
    }

    return <p>{message.content}</p>;
  };

  return (
    <div className="project-chat-view">
      <div className="chat-header">
        <button onClick={onBack} className="back-btn">← Back to Projects</button>
        <div className="header-content">
          <h2>{project.name}</h2>
          <p className="project-description">{project.description}</p>
        </div>
        <button 
          onClick={handleSyncProject} 
          className="sync-btn"
          disabled={isProcessing}
          title="Re-run the initial query to refresh data"
        >
          {isProcessing ? '⟳ Syncing...' : '↻ Sync'}
        </button>
      </div>

      <div className="chat-container">
        <div className="chat-left-panel">
          <div className="file-upload-section">
            <h3>Project Files</h3>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.html"
              onChange={handleFileUpload}
              style={{ display: 'none' }}
              id="file-upload"
            />
            <label htmlFor="file-upload" className="upload-btn">
              <FileUploadIcon /> Upload Files
            </label>
            
            <div className="uploaded-files">
              {uploadedFiles.length === 0 ? (
                <p className="no-files">No files uploaded yet</p>
              ) : (
                <ul>
                  {uploadedFiles.map(file => (
                    <li key={file.file_id} className="file-item">
                      <FileIcon />
                      <span className="file-name">{file.filename}</span>
                      <button
                        className="delete-btn"
                        onClick={() => handleDeleteFile(file.file_id)}
                        title="Delete file"
                      >
                        <TrashIcon />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          </div>

          <div className="chat-section">
            <h3>Conversation</h3>
            <div className="messages-container">
              {console.log('🎨 RENDERING MESSAGES:', messages.length, 'messages')}
              {console.log('🎨 Message types:', messages.map(m => m.type))}
              {messages.map(message => {
                console.log('🎨 Rendering message:', message.type, message.id);
                return (
                  <div key={message.id} className={`message ${message.type}`}>
                    <div className="message-header">
                      <span className="message-type">
                        {message.type === 'user' ? 'You' : 
                         message.type === 'execution_plan' ? 'Execution Plan' :
                         message.type === 'assistant' ? 'Assistant' : 'System'}
                      </span>
                      <span className="message-time">
                        {new Date(message.timestamp).toLocaleTimeString()}
                      </span>
                    </div>
                    <div className="message-content">
                      {renderMessage(message)}
                    </div>
                  </div>
                );
              })}
              
              {/* Show processing status with progress */}
              {isProcessing && queryProgress && (
                <div className="message system processing-status">
                  <div className="message-header">
                    <span className="message-type">System</span>
                    <span className="message-time">
                      {new Date().toLocaleTimeString()}
                    </span>
                  </div>
                  <div className="message-content">
                    <div className="processing-indicator">
                      <div className="processing-header">
                        <span className="processing-label">🔄 Processing Query</span>
                        <span className="processing-percentage">{queryProgress.progress || 0}%</span>
                      </div>
                      
                      <div className="progress-bar-container">
                        <div 
                          className="progress-bar-fill" 
                          style={{ width: `${queryProgress.progress || 0}%` }}
                        />
                      </div>
                      
                      {queryProgress.currentAgent && (
                        <div className="current-agent">
                          <span className="agent-label">Current Agent:</span>
                          <span className="agent-name">
                            {queryProgress.currentAgent.replace(/CX Insights/gi, 'Customer Insights')}
                          </span>
                        </div>
                      )}
                      
                      {queryProgress.currentStep && (
                        <div className="current-step">
                          <span className="step-label">Step:</span>
                          <span className="step-text">
                            {queryProgress.currentStep
                              .replace(/CX Insights/gi, 'Customer Insights')
                              .replace('Customer Insights Analyst analysis', 'Customer Insights analysis')}
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              )}
              
              <div ref={messagesEndRef} />
            </div>

            <div className="chat-input">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                placeholder="Type your query or refinement..."
                disabled={isProcessing}
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim() || isProcessing}
                className="send-btn"
              >
                <SendIcon />
              </button>
            </div>
          </div>
        </div>

        <div className="results-sidebar">
          <div className="results-header">
            <h3>Query Results</h3>
            <div className="results-controls">
              <button onClick={() => setDebugMode(!debugMode)} className="debug-toggle-btn">
                {debugMode ? 'Hide Debug' : 'Show Debug'}
              </button>
              {queryResult && (
                <div className="export-buttons">
                  <button onClick={() => handleExportPDF(queryResult, 'fab')} className="export-btn pdf-btn" title="Export FAB branded PDF">
                    Export PDF
                  </button>
                  <button onClick={() => handleExportPDF(queryResult, 'columbus')} className="export-btn pdf-btn" title="Export Columbus professional PDF">
                    Export CPDF
                  </button>
                  <button onClick={() => handleExportPPTX(queryResult)} className="export-btn pptx-btn">
                    Export PPTX
                  </button>
                </div>
              )}
            </div>
          </div>
          
          {/* Debug Panel */}
          {debugMode && (
            <div className="debug-panel">
              <h4>Debug Information</h4>
              <div className="debug-content">
                <p>Query ID: {currentQueryId}</p>
                <p>Status: {queryProgress?.status || 'N/A'}</p>
                <p>Progress: {queryProgress?.progress || 0}%</p>
                <p>Current Agent: {queryProgress?.currentAgent || 'N/A'}</p>
                <details>
                  <summary>Raw Data</summary>
                  <pre>{JSON.stringify(queryResult, null, 2)}</pre>
                </details>
              </div>
            </div>
          )}
          
          {isProcessing && queryProgress && (
            <div className="progress-section">
              <h4>Processing Query</h4>
              <div className="progress-bar">
                <div 
                  className="progress-fill"
                  style={{ width: `${queryProgress.progress || 0}%` }}
                />
              </div>
              <p className="progress-status">
                {queryProgress.currentStep || queryProgress.message || 'Processing...'}
              </p>
              {queryProgress.currentAgent && (
                <p className="current-agent">
                  Current Agent: {queryProgress.currentAgent}
                </p>
              )}
            </div>
          )}

          {queryResult && (
            <div className="result-section">
              <h4>Analysis Complete</h4>
              {renderQueryResult(queryResult)}
            </div>
          )}

          {!isProcessing && !queryResult && (
            <div className="empty-results">
              <p>Results will appear here once you submit a query</p>
            </div>
          )}
        </div>
      </div>

      {/* PDF Export Dialog */}
      {showPDFDialog && queryResult && (
        <PDFExportDialog
          isOpen={showPDFDialog}
          onClose={() => setShowPDFDialog(false)}
          onExport={handlePDFExportWithOptions}
          availableSections={
            queryResult.areas 
              ? [...Object.keys(queryResult.areas), 'executive_summary']
              : ['executive_summary']
          }
          queryResult={queryResult}
        />
      )}
    </div>
  );
};

export default ProjectChatView;
